import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import TwoLineDocNo from "@/components/TwoLineDocNo";
import { toast } from "sonner";
import { getUser } from "@/lib/secureStorage";
import { hasPerm } from "@/lib/auth";
import { HoldReleaseButtons, OnHoldBadge } from "@/components/WoHoldRelease";
import { ShortCloseButton } from "@/components/WoShortClose";
import { canSubmitDoc } from "@/lib/submitPerms";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { PlusIcon, TrashIcon, CheckCircleIcon, ProhibitIcon, PencilSimpleIcon, PrinterIcon, NotePencilIcon, EyeIcon, TreeStructureIcon, StackIcon, WarningIcon, ClockCounterClockwiseIcon, WrenchIcon, PaperPlaneTiltIcon, MagnifyingGlassIcon } from "@phosphor-icons/react";

import FormattedNumberInput from "@/components/FormattedNumberInput";
import { fmtDateIST } from "@/lib/datetime";
import ModifyDocumentPopover from "@/components/ModifyDocumentPopover";
import { WoRejectDialog } from "@/components/WoRejectDialog";
import { fulfilmentOf, isStockOrder } from "@/components/StockBadge";
/*
 Work Orders (Feb 2026 · Phase 1)
 --------------------------------
 One page: KPI strip · list · full-form dialog.
 The dialog picks a Sales Order (SO) or Export Sales Order (ESO) + a specific
 line item, then auto-loads customer / item / ordered qty / delivery from the
 source. Planner types rejection allowance %, extra, sampling and setup loss;
 Production Qty updates live. Draft is savable; Approve is gated on the
 backend rule (production_qty >= ordered_qty).
 File is still named ProductionOrders.jsx and the DB field is `po_no` for
 backward compatibility; the entire user-facing surface says "Work Order".
*/

const inp = "w-full h-9 px-3 bg-[var(--bg-0)] border border-[var(--border)] rounded-sm text-white text-sm focus:outline-none focus:border-[var(--accent)]";
const selCls = "bg-[var(--bg-0)] border-[var(--border)] text-white h-9";
const STATUS_TONE = {
  Draft: "bg-zinc-500/15 text-zinc-300 border-zinc-500/30",
  Approved: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
  Cancelled: "bg-red-500/15 text-red-300 border-red-500/30",
  Running: "bg-sky-500/15 text-sky-300 border-sky-500/30",
  "Short Closed": "bg-orange-500/15 text-orange-300 border-orange-500/30",
  Completed: "bg-teal-500/15 text-teal-300 border-teal-500/30",
};
// WO-STATUS-BUFFER-01 · whole-metre buffer (half-up), mirrors backend `round_whole_m`.
const roundWholeM = (x) => Math.round(Number((+x || 0).toFixed(6)));
const fmtBuf = (v) => { const n = +v || 0; return Number.isInteger(n) ? n.toLocaleString("en-IN") : n.toFixed(2); };
const woLife = (o) => o?.lifecycle_status || o?.status;

// P1-WO-MANAGEMENT-AUTH-CONTROL · Phase 2C · Path B · Feb 2026 —
// canonical Management Authorisation state helpers. The sealed WO
// `status` vocabulary is UNCHANGED — these helpers key off the
// additive `approval_status` axis and are the single source of truth
// for row + View mode surfaces.
const WO_APPROVAL_TONE = {
  "Not Submitted":    "bg-zinc-500/15 text-zinc-300 border-zinc-500/30",
  "Pending Approval": "bg-amber-500/15 text-amber-300 border-amber-500/30",
  "Rejected":         "bg-red-500/15 text-red-300 border-red-500/30",
  "Approved":         "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
};
const WO_APPROVAL_LABEL = {
  "Not Submitted":    "Not Submitted",
  "Pending Approval": "Waiting for Auth",
  "Rejected":         "Rejected",
  "Approved":         "Authorised",
};
const woApproval = (o) => (o?.approval_status || "").trim();
const isWoNotSubmitted = (o) => {
  const a = woApproval(o);
  return (a === "" || a === "Not Submitted") && o?.status !== "Approved" && o?.status !== "Cancelled";
};
const isWoPending    = (o) => woApproval(o) === "Pending Approval" && o?.status !== "Cancelled";
const isWoRejected   = (o) => woApproval(o) === "Rejected" && o?.status !== "Cancelled";
const isWoAuthorised = (o) => o?.status === "Approved";

// Feb 2026 · Unit-aware number formatters used across the WO Explode
// dialog. kg values always render with 3 decimals, gms with 2 — a
// company-wide house rule so `5` never appears as `5` next to `5.00`.
const fmtKg = (n) => Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 3, maximumFractionDigits: 3 });
const fmtG  = (n) => Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
// Feb 2026 · MRP execution chip tones — shown next to the workflow status
// so planners can spot Work Orders that still need an MRP run at a glance.
const MRP_TONE = {
  EXECUTED:   "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
  CALCULATED: "bg-sky-500/15 text-sky-300 border-sky-500/30",
  PENDING:    "bg-amber-500/15 text-amber-300 border-amber-500/30",
};
// Feb 2026 · BOM Explosion chip tones — signals whether child work orders
// have been generated. EXPLODED = at least one CWO; PENDING otherwise.
const BOM_TONE = {
  EXPLODED: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
  PENDING:  "bg-amber-500/15 text-amber-300 border-amber-500/30",
};

// Sep 03, 2026 · Loom Allocation chip tones — one-glance signal on how
// much of a WO's ordered qty has been assigned to physical looms, and
// whether any of those looms are actively running production entries.
//   Running    · at least one linked plan is currently in "Running" state
//   Fully      · Σ planned_qty ≥ WO production_qty (or ordered_qty fallback)
//   Partial    · 0 < Σ planned_qty < required qty
//   Unallocated · no plans linked to this WO
const ALLOC_TONE = {
  Running:     "bg-sky-500/15 text-sky-300 border-sky-500/40",
  Fully:       "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
  Partial:     "bg-amber-500/15 text-amber-300 border-amber-500/30",
  Unallocated: "bg-zinc-500/15 text-zinc-400 border-zinc-500/30",
};
const ALLOC_LABEL = {
  Running: "Running",
  Fully: "Fully Allocated",
  Partial: "Partial",
  Unallocated: "Unallocated",
};

// WO Register UI/UX V1 (Jun 2026) — config-driven line-wise register
// modelled on the sealed Sales Order Register V1. Presentation only; no
// backend/API/DB/business-logic change. Customer/Buyer deliberately OMITTED
// (Production Floor Privacy) — not offered in columns or search.
const WO_COL_ORDER = [
  "wo_no", "date", "src", "source_order", "item_no", "style_no", "product_code",
  "ordered", "buffer", "prod_qty", "produced", "pending", "target_date",
  "status", "allocation", "mrp", "bom",
  // optional (hidden by default, available via Columns ▾)
  "priority", "looms", "produced_pct", "mrp_no", "cwo_count", "remarks",
  "actions",
];
const WO_PINNED_COLS = new Set(["wo_no", "actions"]);
const WO_DEFAULT_HIDDEN = new Set(["priority", "looms", "produced_pct", "mrp_no", "cwo_count", "remarks"]);
// Secondary authorization label tone (muted) for the two-tier Status cell.
const WO_APPROVAL_SEC_TONE = {
  "Not Submitted": "text-zinc-500",
  "Waiting for Auth": "text-amber-300/80",
  "Rejected": "text-red-300/80",
  "Authorised": "text-emerald-300/80",
};

const F = ({ label, children, hint }) => (
  <div className="space-y-1">
    <label className="text-[10px] uppercase tracking-widest text-zinc-500">{label}</label>
    {children}
    {hint && <div className="text-[10px] text-zinc-600">{hint}</div>}
  </div>
);
const RO = ({ label, value, testid }) => (
  <div className="space-y-1">
    <label className="text-[10px] uppercase tracking-widest text-zinc-500">{label}</label>
    <div data-testid={testid} className="h-9 px-3 flex items-center bg-[var(--bg-2)]/40 border border-[var(--border)] rounded-sm text-white text-sm font-mono">
      {value === 0 || value ? value : "—"}
    </div>
  </div>
);

const emptyForm = () => ({
  po_no: "", date: new Date().toISOString().slice(0, 10),
  source_type: "SO", source_order_id: "", source_order_no: "", source_line_id: "",
  customer_id: "", customer_name: "",
  item_id: "", item_code: "", item_description: "",
  ordered_qty: 0, delivery_date: "",
  // Feb 2026 · SO context fields (auto-loaded, read-only on the WO screen)
  sales_date: "", po_number: "", priority: "", so_status: "", special_instructions: "",
  rejection_allowance_pct: 2,
  rejection_allowance_qty: 0, production_qty: 0,
  // Feb 2026 · line-wise data — parity with Sales Orders. One entry per
  // SO/ESO line with its own rejection allowance % and extra qty override.
  items: [],
  status: "Draft", remarks: "",
});

// Default line-wise Rejection Allowance %. Applied to every SO line when the
// planner first picks the source order. Individual lines can override.
const DEFAULT_LINE_REJ_PCT = 2;

export default function ProductionOrders() {
  const [rows, setRows] = useState([]);
  const [machines, setMachines] = useState([]);
  const [plans, setPlans] = useState([]);
  const [sos, setSos] = useState([]);
  const [esos, setEsos] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [items, setItems] = useState([]);
  // Feb 2026 · Routing chip strip · resolved from the picked article's
  // `production_routing`. Null until a Product Code is chosen.
  const [routingHint, setRoutingHint] = useState(null);
  const [postProdElig, setPostProdElig] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm());
  const [editId, setEditId] = useState(null);
  // Feb 2026 · Effects placed AFTER `form` + `editId` are declared so
  // both are defined at first render (avoids the temporal-dead-zone
  // ReferenceError we hit when they lived above their state).
  useEffect(() => { if (!open) setRoutingHint(null); }, [open]);
  useEffect(() => {
    if (!open) return;
    const code = form.item_code || "";
    if (!code) { setRoutingHint(null); return; }
    api.get(`/production-routing/by-code/${encodeURIComponent(code)}`)
      .then(({ data }) => setRoutingHint(data || null))
      .catch(() => setRoutingHint(null));
  }, [open, form.item_code]);
  // Feb 2026 · Load Post-Production Job Card eligibility on open.
  // Weaving must be In-Process / Completed before Dyeing/Finishing +
  // Checking & Packing instruction cards can be spawned.
  useEffect(() => {
    if (!open || !editId) { setPostProdElig(null); return; }
    api.get(`/work-orders/${editId}/post-production/eligibility`)
      .then(({ data }) => setPostProdElig(data || null))
      .catch(() => setPostProdElig(null));
  }, [open, editId]);
  const refreshPostProdElig = async () => {
    if (!editId) return;
    try {
      const { data } = await api.get(`/work-orders/${editId}/post-production/eligibility`);
      setPostProdElig(data || null);
    } catch { /* silent */ }
  };
  const genPostProdJC = async (stage_code) => {
    try {
      const { data } = await api.post(`/work-orders/${editId}/post-production/job-card`, { stage_code });
      toast.success(data.existing ? `${data.job_card_no} already exists` : `Generated ${data.job_card_no}`);
      refreshPostProdElig();
    } catch (e) { toast.error(e?.response?.data?.detail || "Job Card generation failed"); }
  };
  // View-only flag — when true, the same dialog opens but every input is
  // read-only. Used for Approved / Cancelled rows so planners can inspect
  // without accidentally amending. Cleared when the dialog closes.
  const [viewOnly, setViewOnly] = useState(false);
  // Phase 2C-6B3A · controlled Work Order cancellation. `cancelPrompt` holds
  // the WO being cancelled (opens the confirmation dialog); `cancelReason` is
  // the mandatory, backend-validated reason. Cancellation is never executed
  // merely by opening the dialog.
  const [cancelPrompt, setCancelPrompt] = useState(null);
  const [cancelReason, setCancelReason] = useState("");
  const [cancelBusy, setCancelBusy] = useState(false);
  const [rejectWo, setRejectWo] = useState(null);  // OD-13 · WO-screen Reject
  // Feb 2026 · Dynamic Order Modification — shared popover for WOs.
  const [modifyOpen, setModifyOpen] = useState(false);
  const [filterStatus, setFilterStatus] = useState("all");
  // WO Register V1 · search · columns · density toolbar state (presentation).
  const [woSearch, setWoSearch] = useState("");
  const [woShowColsMenu, setWoShowColsMenu] = useState(false);
  const [woVisibleCols, setWoVisibleCols] = useState(() => {
    try {
      const raw = localStorage.getItem("mes.wo.register.cols.v1");
      if (raw) return new Set(JSON.parse(raw));
    } catch (e) { /* ignore */ }
    return new Set(WO_COL_ORDER.filter(k => !WO_DEFAULT_HIDDEN.has(k)));
  });
  const toggleWoCol = (k) => setWoVisibleCols(prev => {
    if (WO_PINNED_COLS.has(k)) return prev;
    const next = new Set(prev);
    next.has(k) ? next.delete(k) : next.add(k);
    try { localStorage.setItem("mes.wo.register.cols.v1", JSON.stringify(Array.from(next))); } catch (e) { /* ignore */ }
    return next;
  });
  const woResetCols = () => {
    const next = new Set(WO_COL_ORDER.filter(k => !WO_DEFAULT_HIDDEN.has(k)));
    setWoVisibleCols(next);
    try { localStorage.setItem("mes.wo.register.cols.v1", JSON.stringify(Array.from(next))); } catch (e) { /* ignore */ }
  };
  const [woDensity, setWoDensity] = useState(() => {
    try { return localStorage.getItem("mes.wo.register.density") || "comfortable"; }
    catch { return "comfortable"; }
  });
  const setWoDensityPersist = (d) => {
    setWoDensity(d);
    try { localStorage.setItem("mes.wo.register.density", d); } catch (e) { /* ignore */ }
  };
  // Feb 2026 · Phase 2 — Explode BOM modal state. Opens a grouped page
  // that rolls up RM requirements + Routing Stage totals across every
  // line in the WO, and lets the planner emit Child Work Orders per
  // stage in one click. `explWo` carries the parent WO doc; `explData`
  // is the /explode-bom response; `childWos` is the linked CWO list.
  const [explWo, setExplWo] = useState(null);
  const [explData, setExplData] = useState(null);
  const [childWos, setChildWos] = useState([]);
  const [explLoading, setExplLoading] = useState(false);
  const [genLoading, setGenLoading] = useState(false);
  // WO-AMEND-HISTORY-UI-H01 · Feb 2026 · Frontend-only Amendment History
  // dialog state. Renders read-only history data ALREADY present on the
  // WO document (top-level lifecycle scalars + embedded `amendments[]`).
  // No new API contract, no backend change, no new RBAC vocabulary — the
  // dialog piggy-backs on the same list payload that already permits the
  // row to render. Owner-mandated event-classification map (Decision §3):
  //   mgmt_reject                → "Management Rejection"
  //   mgmt_reject_resubmit       → "Correction & Resubmission"
  //   reconcile_reject_lifecycle → "Defect Reconciliation (Technical)"
  //   other / absent             → "Business Amendment"
  const [historyWo, setHistoryWo] = useState(null);
  // Feb 2026 · when a source order is picked we auto-fetch ALL its lines so
  // the WO can carry one row per SO line (user request). Kept as its own
  // state so the dialog can render a readonly grid alongside the single-line
  // snapshot logic that already exists.
  const [sourceLines, setSourceLines] = useState([]);
  const navigate = useNavigate();

  // Feb 2026 · WO Allocation index — one bucket per WO number.
  // Prior revisions keyed by `(source_order_id, item_id)` with an SO-wide
  // fallback, which meant every WO spawned from the same ESO shared the
  // exact same loom list — WO-00013 would render NL-016 just because a
  // sibling WO for the same ESO was on the Gantt. The healing migration
  // (m0007) now stamps `plan.source_wo_no` on every plan, so we match on
  // that directly. No fallback: a WO with no plan carrying its `wo_no`
  // shows a BLANK machine column, per the user rule.
  const allocByWo = useMemo(() => {
    const machineById = Object.fromEntries((machines || []).map(m => [m.id, m]));
    const acc = {};
    for (const pl of (plans || [])) {
      const wono = (pl.source_wo_no || "").trim();
      if (!wono) continue;                                   // no wo → skip
      if (!acc[wono]) acc[wono] = { looms: [], loomIds: new Set(), allocated_qty: 0, is_running: false };
      const b = acc[wono];
      if (pl.machine_id && !b.loomIds.has(pl.machine_id)) {
        b.loomIds.add(pl.machine_id);
        const m = machineById[pl.machine_id];
        b.looms.push({
          id: pl.machine_id,
          code: (m && m.code) || pl.machine_id.slice(0, 8),
          status: (m && m.status) || "",
        });
      }
      b.allocated_qty += Number(pl.planned_qty || 0);
      if (String(pl.status || "").toLowerCase() === "running") b.is_running = true;
    }
    return acc;
  }, [plans, machines]);

  // Resolve one WO row's allocation snapshot. Strict match by WO No —
  // no SO-wide fallback so a WO absent from the Gantt renders blank.
  const allocationFor = (r) => {
    const woNo = (r.wo_no || r.po_no || "").trim();
    const info = (woNo && allocByWo[woNo]) || { looms: [], allocated_qty: 0, is_running: false };
    const required = Number(r.production_qty || r.ordered_qty || 0);
    let state = "Unallocated";
    if (info.is_running) state = "Running";
    else if (info.looms.length === 0) state = "Unallocated";
    else if (required > 0 && info.allocated_qty + 1e-6 >= required) state = "Fully";
    else state = "Partial";
    return { ...info, required, state };
  };

  // Feb 2026 · Produced Qty + Pending Qty per WO. Sums `actual_qty`
  // across every plan whose `source_wo_no` matches the WO No (denorm'd
  // by `_enrich_plan_dna` + migration m0010). Pending = Prod Qty − Produced
  // (never negative). Used in the two new grid columns below.
  const producedByWo = useMemo(() => {
    const acc = {};
    for (const p of (plans || [])) {
      const key = (p.source_wo_no || "").trim();
      if (!key) continue;
      acc[key] = (acc[key] || 0) + (+p.actual_qty || 0);
    }
    return acc;
  }, [plans]);

  const load = async () => {
    const [r, s, e, c, it, m, p] = await Promise.all([
      api.get("/work-orders/"),
      api.get("/sales-orders/"),
      api.get("/exports/orders/").catch(() => ({ data: [] })),
      api.get("/customers/"),
      api.get("/items/"),
      // Sep 03, 2026 · Loom Allocation columns — fold the Machine
      // Master + Production Plans into the WO grid so operators see
      // which looms carry each WO without leaving the page.
      api.get("/machines/").catch(() => ({ data: [] })),
      api.get("/production-plans/").catch(() => ({ data: [] })),
    ]);
    setRows(r.data);
    setMachines(m.data || []);
    setPlans(p.data || []);
    // Only Approved / authorised SOs qualify — spec step 1 = "Sales Order Approved".
    setSos((s.data || []).filter(o => o.authorized || o.status === "Approved" || o.status === "Open" || o.status === "Planned"));
    // Normalise ESO records to the SO shape the picker expects so we can
    // reuse the same UI. Feb 2026.
    //   ESO uses `doc_no` (not `order_no`), `buyer_customer_id` + `buyer_name`
    //   (not `customer_id`), and items[] without `line_id` — we synthesise
    //   `idx-<i>` so React keys stay stable and the backend can echo them
    //   back on submit.
    const normEsos = (e.data || []).map(o => ({
      ...o,
      order_no: o.doc_no || o.order_no,
      customer_id: o.buyer_customer_id || o.customer_id || "",
      items: (o.items || []).map((li, i) => ({
        ...li,
        line_id: li.line_id || `idx-${i}`,
        order_qty: li.order_qty || li.qty || 0,
        unit: li.unit || li.uom || "meter",
      })),
    }));
    setEsos(normEsos);
    setCustomers(c.data); setItems(it.data);
  };
  useEffect(() => { load(); }, []);

  // Feb 2026 · Resolve the 1-based line number of a WO relative to its
  // parent SO/ESO. Mirrors the "Item No" column shown on Sales Orders
  // so operators reading a WO can jump straight to the matching SO row.
  // Falls back to `idx-<n>` decoding for legacy ESO synth line ids.
  const woItemNo = (r) => {
    const src = (r.source_type === "ESO" ? esos : sos).find(o => o.id === r.source_order_id);
    const items = src?.items || [];
    const idx = items.findIndex(li => li.line_id === r.source_line_id);
    if (idx >= 0) return String(idx + 1).padStart(3, "0");
    if ((r.source_line_id || "").startsWith("idx-")) {
      const n = parseInt(r.source_line_id.split("-")[1], 10);
      if (!isNaN(n)) return String(n + 1).padStart(3, "0");
    }
    return "—";
  };

  // Feb 2026 · lock duplicate SO/ESO line pickup. A key like
  // `SO|<order_id>|<line_id>` is added to this Set for every non-Cancelled
  // Production Order EXCEPT the one currently being edited. The Product Code
  // dropdown greys out lines in the Set and the SO/ESO dropdown greys out
  // orders where every line is locked. Backend also enforces this on POST/PUT.
  const lockedKeys = useMemo(() => {
    const s = new Set();
    for (const r of rows) {
      if (r.status === "Cancelled") continue;
      if (editId && r.id === editId) continue;
      s.add(`${r.source_type}|${r.source_order_id}|${r.source_line_id}`);
    }
    return s;
  }, [rows, editId]);

  const isLineLocked = (order_id, line_id) =>
    lockedKeys.has(`${form.source_type}|${order_id}|${line_id}`);

  const isOrderFullyLocked = (order) => {
    const lines = order.items || [];
    if (!lines.length) return false;
    return lines.every((li, i) => {
      const lid = li.line_id || `idx-${i}`;
      return isLineLocked(order.id, lid);
    });
  };

  const lockedPoFor = (order_id, line_id) => {
    const key = `${form.source_type}|${order_id}|${line_id}`;
    return rows.find(r => r.status !== "Cancelled" && r.id !== editId
      && `${r.source_type}|${r.source_order_id}|${r.source_line_id}` === key);
  };

  // Feb 2026 · Authorization Gate — WO create dropdown lists ONLY
  // Authorized SOs / ESOs. Accepts every field convention used across
  // the codebase — SOs flip `authorized: true` or `status → Approved`,
  // while ESOs use `auth_status → "Authorised"` (British spelling).
  const isAuthorized = (o) =>
    o?.authorized === true ||
    ["Approved", "Authorized"].includes(o?.status) ||
    ["Authorised", "Authorized"].includes(o?.auth_status);
  // SOESO-FULFIL-01 · Sales Only orders are never eligible for WO pickup (backend enforces too).
  const sourceOptions = (form.source_type === "ESO" ? esos.filter(o => o?.status !== "Short Closed") : sos).filter(isAuthorized).filter(o => fulfilmentOf(o) !== "sales_only");  // IMP-0B · decision B
  const currentSourceOrder = useMemo(
    () => sourceOptions.find(o => o.id === form.source_order_id),
    [sourceOptions, form.source_order_id]
  );
  // Available line items on the picked source order.
  const currentLines = currentSourceOrder?.items || [];

  // Fetches the preview snapshot for a given (type, order_id, line_id) and
  // merges it into the form. Kept as its own function so `onPickOrder` can
  // safely auto-pick a single-line order without waiting for React to flush
  // the earlier setForm — passing the args explicitly dodges stale-closure
  // reads on `form.source_order_id`.
  const fetchPreview = async (source_type, order_id, line_id) => {
    if (!order_id || !line_id) return;
    try {
      const res = await api.get("/work-orders/source-preview", {
        params: { source_type, order_id, line_id },
      });
      setForm(f => ({ ...f, ...res.data, source_line_id: line_id }));
      // Also refresh the routing chip strip for the newly-adopted first line.
      const line = ((res.data.items || res.data.item_list || [])[0]) || null;
      const code = res.data.item_code || line?.article_no || "";
      if (code) {
        api.get(`/production-routing/by-code/${encodeURIComponent(code)}`)
          .then(({ data }) => setRoutingHint(data || null))
          .catch(() => setRoutingHint(null));
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Preview failed");
    }
  };

  // Auto-load snapshot fields when the planner picks a source line.
  const onPickLine = (line_id) => {
    setForm(f => ({ ...f, source_line_id: line_id }));
    // Feb 2026 · Routing chip strip — resolve the selected line's Article
    // by its Product Code and paint the departmental sequence (Warping →
    // Weaving → Finishing/Dyeing-C&P) so the planner sees the path the
    // shop floor will follow. Empty when the article's routing isn't set.
    try {
      const line = (currentLines || []).find(li => (li.line_id || "") === line_id);
      const code = line?.article_no || items.find(x => x.id === line?.item_id)?.description || items.find(x => x.id === line?.item_id)?.code || "";
      if (code) {
        api.get(`/production-routing/by-code/${encodeURIComponent(code)}`)
          .then(({ data }) => setRoutingHint(data || null))
          .catch(() => setRoutingHint(null));
      } else {
        setRoutingHint(null);
      }
    } catch { setRoutingHint(null); }
    fetchPreview(form.source_type, form.source_order_id, line_id);
  };
  const onPickOrder = (order_id) => {
    const o = sourceOptions.find(x => x.id === order_id);
    setForm(f => ({
      ...f, source_order_id: order_id, source_order_no: o?.order_no || "",
      source_line_id: "",
      // Clear the snapshot so we don't display stale values before line pick.
      customer_id: "", customer_name: "", item_id: "", item_code: "",
      item_description: "", ordered_qty: 0, delivery_date: "",
      items: [],
    }));
    // Feb 2026 · fetch ALL lines of the picked order so the WO carries one
    // items[] row per SO line (parity with Sales Orders — user request).
    setSourceLines([]);
    if (order_id) {
      api.get(`/work-orders/source-lines`, { params: { source_type: form.source_type, order_id } })
        .then(res => {
          const lines = res.data?.lines || [];
          setSourceLines(lines);
          // Seed form.items[] with a per-line rejection allowance so each
          // line contributes its own Extra Qty toward Production Qty.
          const seeded = lines.map(li => {
            const extra = roundWholeM(+li.extra_qty || (+li.ordered_qty || 0) * DEFAULT_LINE_REJ_PCT / 100);
            return {
              line_id: li.line_id,
              item_id: li.item_id,
              style_no: li.style_no || "",
              product_code: li.product_code,
              item_description: li.item_description,
              order_qty: +li.ordered_qty || 0,
              rejection_allowance_pct: DEFAULT_LINE_REJ_PCT,
              extra_qty: extra,
              total_qty: (+li.ordered_qty || 0) + extra,
              delivery_date: li.delivery_date || "",
              unit: li.unit || "meter",
            };
          });
          setForm(f => ({ ...f, items: seeded }));
          // Auto-pick the first line as the primary snapshot so header
          // context (customer, delivery date, SO status) populates without
          // an extra click.
          if (seeded.length > 0) {
            fetchPreview(form.source_type, order_id, seeded[0].line_id);
          }
        })
        .catch(() => setSourceLines([]));
    }
  };
  const onSwitchSource = (type) => {
    setForm(f => ({
      ...f, source_type: type, source_order_id: "", source_order_no: "", source_line_id: "",
      customer_id: "", customer_name: "", item_id: "", item_code: "",
      item_description: "", ordered_qty: 0, delivery_date: "",
    }));
  };

  // ── Live compute (mirrors backend `_compute`) ──
  // When items[] is populated, totals come from summing per-line values so
  // planners see the same Production Qty the backend will store. Legacy
  // single-line fallback (no items) still uses the old formula.
  const lineTotals = useMemo(() => {
    const its = form.items || [];
    if (!its.length) return null;
    const ord = its.reduce((s, l) => s + (+l.order_qty || 0), 0);
    const rej = its.reduce((s, l) => s + (+l.extra_qty || 0), 0);
    return {
      ordered: Math.round(ord * 100) / 100,
      rejQty: Math.round(rej * 100) / 100,
      prodQty: Math.round((ord + rej) * 100) / 100,
    };
  }, [form.items]);
  const rejAllowanceQty = lineTotals
    ? lineTotals.rejQty
    : roundWholeM((+form.ordered_qty || 0) * (+form.rejection_allowance_pct || 0) / 100);
  const productionQty = lineTotals
    ? lineTotals.prodQty
    : Math.round(((+form.ordered_qty || 0) + rejAllowanceQty) * 100) / 100;
  const totalOrdered = lineTotals ? lineTotals.ordered : (+form.ordered_qty || 0);

  const problems = useMemo(() => {
    const p = [];
    if (!form.source_order_id) p.push("Pick a Sales Order / Export Order");
    if (!(form.items && form.items.length)) p.push("No line items — pick an SO/ESO with at least one product");
    if (totalOrdered <= 0) p.push("Total Ordered Qty is zero — check line items");
    if (productionQty < totalOrdered) p.push("Production Qty must be ≥ Total Ordered Qty");
    return p;
  }, [form, productionQty, totalOrdered]);

  // Update a single line item's field. If the planner edits Rejection
  // Allowance %, Extra Qty auto-recalculates for that line. If they edit
  // Extra Qty directly, the % is left as-is (manual override wins).
  const updateLine = (i, key, val) => {
    setForm(f => {
      const its = [...(f.items || [])];
      const li = { ...its[i], [key]: val };
      const order = +li.order_qty || 0;
      if (key === "rejection_allowance_pct") {
        li.extra_qty = roundWholeM(order * (+val || 0) / 100);
      } else if (key === "order_qty") {
        li.extra_qty = roundWholeM(order * (+li.rejection_allowance_pct || 0) / 100);
      } else if (key === "extra_qty") {
        li.extra_qty = roundWholeM(val);
      }
      li.total_qty = Math.round((order + (+li.extra_qty || 0)) * 100) / 100;
      its[i] = li;
      return { ...f, items: its };
    });
  };

  const openNew = () => { setEditId(null); setViewOnly(false); setForm(emptyForm()); setOpen(true); };
  const openEdit = (row) => {
    setEditId(row.id);
    setViewOnly(false);
    setForm({ ...emptyForm(), ...row });
    setOpen(true);
  };
  // View — same dialog, everything read-only. Feb 2026.
  const openView = (row) => {
    setEditId(row.id);
    setViewOnly(true);
    setForm({ ...emptyForm(), ...row });
    setOpen(true);
  };

  const save = async (submitAfter = false) => {
    try {
      const payload = {
        ...form,
        rejection_allowance_qty: rejAllowanceQty,
        production_qty: productionQty,
        ordered_qty: totalOrdered,
        items: form.items || [],
      };
      const res = editId
        ? await api.put(`/work-orders/${editId}`, payload)
        : await api.post("/work-orders/", payload);
      // Feb 2026 · Multi-line SOs auto-split into N WOs; the endpoint
      // returns { split, count, created:[...], skipped:[...] } in that
      // case. Legacy single-doc responses still work as-is.
      const isSplit = res.data && Array.isArray(res.data.created);
      const wos = isSplit ? res.data.created : [res.data];
      // P1-WO Phase 2C · Path B · Feb 2026 — Save & Submit now goes
      // through the canonical submit-for-approval route. Direct
      // authorise-from-Draft is retired; Management authorises from
      // the queue.
      if (submitAfter) {
        for (const w of wos) {
          if (w.status === "Draft" && !isWoAuthorised(w)) {
            await api.post(`/work-orders/${w.id}/submit-for-approval`);
          }
        }
      }
      if (isSplit) {
        const suffix = res.data.skipped?.length ? ` · ${res.data.skipped.length} line(s) already linked, skipped` : "";
        toast.success(`${wos.length} Work Order(s) created — one per SO line${suffix}${submitAfter ? " · submitted for approval" : ""}`);
      } else {
        toast.success(`${wos[0].po_no} · ${submitAfter ? "submitted for approval" : "saved"}`);
      }
      setOpen(false); load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
  };

  const approve = async (row) => {
    try { await api.post(`/work-orders/${row.id}/approve`); toast.success(`${row.po_no} approved`); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Approve failed"); }
  };

  // P1-WO-MANAGEMENT-AUTH-CONTROL · Phase 2C · Path B · Feb 2026 —
  // canonical Submit / Resubmit handler. Row + View mode invoke the
  // SAME function so the UI has a single source of truth (parity
  // with SO Phase 2B). Rejected WOs render as Resubmit copy.
  const submitForApproval = async (row) => {
    if (!row?.id) return;
    const isResubmit = isWoRejected(row);
    const verb = isResubmit ? "Resubmit" : "Submit";
    const label = isResubmit
      ? `Resubmit ${row.po_no} for Management Re-authorisation?`
      : `Submit ${row.po_no} for Management Authorisation?`;
    if (!window.confirm(label)) return;
    try {
      await api.post(`/work-orders/${row.id}/submit-for-approval`);
      toast.success(`${row.po_no} · ${verb === "Resubmit" ? "resubmitted" : "submitted"} for approval`);
      setOpen(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || `${verb} failed`);
    }
  };

  // P1-WO-MANAGEMENT-AUTH-CONTROL · Phase 2C · Path B · Feb 2026 —
  // Bulk Submit-for-Approval. Under Path B a Draft WO cannot be
  // directly authorised — it must be Submitted first, then Management
  // authorises from the queue. This helper submits every Not-Submitted
  // / Rejected WO in the filtered view so planners still get a
  // one-click "prepare all these for Management" flow.
  const bulkApprove = async () => {
    const targets = filtered.filter(r => isWoNotSubmitted(r) || isWoRejected(r));
    if (targets.length === 0) return toast.info("No Work Orders to submit for approval");
    if (!window.confirm(`Submit ${targets.length} Work Order${targets.length > 1 ? "s" : ""} for Management Authorisation?`)) return;
    let ok = 0, failed = 0;
    for (const r of targets) {
      try { await api.post(`/work-orders/${r.id}/submit-for-approval`); ok += 1; }
      catch (e) { failed += 1; }
    }
    if (failed === 0) toast.success(`${ok} Work Order${ok > 1 ? "s" : ""} submitted for approval`);
    else toast.error(`${ok} submitted · ${failed} failed`);
    load();
  };
  // Phase 2C-6B3A · open the controlled cancellation dialog (never cancels
  // directly). Only Approved, non-Cancelled WOs are eligible.
  const cancel = (row) => {
    setCancelReason("");
    setCancelPrompt(row);
  };
  // Executes cancellation after a valid reason + explicit confirmation. The
  // backend re-validates the reason and is authoritative.
  const confirmCancel = async () => {
    const row = cancelPrompt;
    if (!row) return;
    const reason = (cancelReason || "").trim();
    if (reason.length < 4) { toast.error("Cancellation reason is required (min 4 characters)"); return; }
    setCancelBusy(true);
    try {
      await api.post(`/production-orders/${row.id}/cancel`, { reason });
      toast.success(`${row.po_no} cancelled`);
      setCancelPrompt(null);
      setCancelReason("");
      setOpen(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Cancel failed");
    } finally {
      setCancelBusy(false);
    }
  };

  // Amend — snapshots the approved PO into its audit trail and reverts to
  // Draft so the planner can edit. Opens the edit dialog right after so it
  // feels like one action. Feb 2026.
  const amend = async (row) => {
    if (!window.confirm(`Amend ${row.po_no}? This will revert it to Draft and log an amendment.`)) return;
    try {
      const res = await api.post(`/work-orders/${row.id}/amend`);
      toast.success(`${row.po_no} · amendment #${res.data.amendment_no} opened`);
      await load();
      openEdit(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Amend failed");
    }
  };

  // Print PDF — Blob download so we can pass the axios auth header, then
  // open the object URL in a new tab. Revoked after 1 min to avoid leaks.
  const printPdf = async (row) => {
    try {
      const res = await api.get(`/work-orders/${row.id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "PDF failed");
    }
  };

  // ── Phase 2 · BOM Explosion + Child Work Orders ──────────────────
  // Fires the multi-item BOM explosion, hydrates the CWO list in
  // parallel, then opens the results modal. Kept idempotent so the
  // planner can re-open the same WO without stale data.
  // Feb 2026 · Bulk-explode every Approved WO with no CWOs yet.
  // Runs explode-bom + child-wos/generate for each — same pair the
  // per-row Explode button fires — so planners can kick off production
  // for every split WO in one click.
  const bulkExplode = async () => {
    const targets = filtered.filter(r => r.status === "Approved" && !(r.cwo_count > 0));
    if (targets.length === 0) return toast.info("No Approved Work Orders without Child WOs to explode");
    if (!window.confirm(`Explode BOM + generate Child WOs for ${targets.length} Approved Work Order${targets.length > 1 ? "s" : ""}?`)) return;
    let ok = 0, failed = 0;
    for (const r of targets) {
      try {
        await api.post(`/work-orders/${r.id}/explode-bom`);
        await api.post(`/work-orders/${r.id}/child-wos/generate`);
        ok += 1;
      } catch (e) { failed += 1; }
    }
    if (failed === 0) toast.success(`${ok} Work Order${ok > 1 ? "s" : ""} exploded — Child WOs generated`);
    else toast.error(`${ok} exploded · ${failed} failed`);
    load();
  };

  const openExplode = async (row) => {
    setExplWo(row);
    setExplData(null);
    setChildWos([]);
    setExplLoading(true);
    try {
      const [ex, cwos] = await Promise.all([
        api.post(`/work-orders/${row.id}/explode-bom`),
        api.get(`/work-orders/${row.id}/child-wos`),
      ]);
      setExplData(ex.data);
      setChildWos(cwos.data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Explode BOM failed");
      setExplWo(null);
    } finally {
      setExplLoading(false);
    }
  };

  const closeExplode = () => { setExplWo(null); setExplData(null); setChildWos([]); setOverrides({}); };

  // Feb 2026 · Per-WO routing overrides — planners can pin Warping /
  // Weaving to a specific floor via the Explode dialog. Loaded from the
  // WO doc's `godown_overrides` on open and saved via a PATCH endpoint.
  const [overrides, setOverrides] = useState({});
  useEffect(() => {
    if (explWo?.godown_overrides) setOverrides(explWo.godown_overrides);
    else setOverrides({});
  }, [explWo]);
  const OVERRIDE_STAGES = [
    { code: "WARPING", label: "Warping",       opts: [["", "Default"], ["WARP-2FLR", "2nd Floor"], ["WARP-3FLR", "3rd Floor"]] },
    { code: "WEAVING", label: "Weaving",       opts: [["", "Default"], ["WEAV-2FLR", "2nd Floor"], ["WEAV-3FLR", "3rd Floor"]] },
  ];
  const saveOverrides = async () => {
    if (!explWo) return;
    try {
      const { data } = await api.patch(`/work-orders/${explWo.id}/routing-overrides`, { overrides });
      toast.success(`Routing pinned · ${data.mrqs_updated} Open MRQ${data.mrqs_updated === 1 ? "" : "s"} re-routed`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
  };

  // Feb 2026 · Print/PDF export of the WO Explode view — server-side
  // ReportLab renders a landscape A4 sheet with letterhead, per-product
  // breakdown, stage roll-up and RM roll-up.
  const printExplodePdf = async () => {
    if (!explWo) return;
    try {
      const res = await api.get(`/work-orders/${explWo.id}/explode-bom/pdf`, { responseType: "blob" });
      window.open(URL.createObjectURL(res.data), "_blank");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Print failed");
    }
  };

  // Creates one Child WO per Routing Stage in the WO's BOMs. Idempotent
  // — re-running merges qty rollups instead of duplicating docs.
  const generateChildWos = async () => {
    if (!explWo) return;
    setGenLoading(true);
    try {
      const res = await api.post(`/work-orders/${explWo.id}/child-wos/generate`);
      const { created = [], updated = [] } = res.data || {};
      toast.success(`Child WOs · ${created.length} created${updated.length ? `, ${updated.length} refreshed` : ""}`);
      const cwos = await api.get(`/work-orders/${explWo.id}/child-wos`);
      setChildWos(cwos.data || []);
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error((d && typeof d === "object" ? d.message || d.code : d) || "Generate Child WOs failed");
    } finally {
      setGenLoading(false);
    }
  };

  // Status flip — cascades on the backend (Completed on a predecessor
  // auto-flips downstream Pending stages to Ready).
  const patchChildWo = async (cwo, status) => {
    try {
      await api.patch(`/child-work-orders/${cwo.id}`, { status });
      toast.success(`${cwo.cwo_no} · ${status}`);
      const cwos = await api.get(`/work-orders/${explWo.id}/child-wos`);
      setChildWos(cwos.data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    }
  };

  // KPI derivations from the current list.
  const kpis = useMemo(() => {
    const draft = rows.filter(r => r.status === "Draft").length;
    const approved = rows.filter(r => r.status === "Approved").length;
    const cancelled = rows.filter(r => r.status === "Cancelled").length;
    const totalPlanQty = rows.reduce((s, r) => s + (+r.production_qty || 0), 0);
    return { draft, approved, cancelled, totalPlanQty };
  }, [rows]);

  const filtered = useMemo(
    () => rows.filter(r => filterStatus === "all" || (filterStatus === "Hold" ? r.on_hold === true : r.status === filterStatus)),
    [rows, filterStatus]
  );

  // ── WO Register V1 · derived helpers + config-driven columns (presentation) ──
  const woProduced = (r) => producedByWo[(r.wo_no || r.po_no || "").trim()] || 0;
  const woStyleOf = (r) => r.style_no || (r.items && r.items[0] && r.items[0].style_no) || "";
  const woTodayISO = new Date().toISOString().slice(0, 10);
  const woCellPad = woDensity === "compact" ? "px-2 py-1.5" : "px-4 py-3";
  const woVisCols = WO_COL_ORDER.filter(k => WO_PINNED_COLS.has(k) || woVisibleCols.has(k));
  const woDisplayed = (() => {
    const q = woSearch.trim().toLowerCase();
    if (!q) return filtered;
    // Customer/Buyer intentionally EXCLUDED from search (Production Floor Privacy).
    return filtered.filter(r => [r.po_no, r.source_order_no, woItemNo(r), woStyleOf(r), r.item_code, r.status, r.lifecycle_status, r.mrp_no]
      .some(v => (v == null ? "" : String(v)).toLowerCase().includes(q)));
  })();

  // Single Actions ▾ dropdown per row — reuses EXACT existing handlers +
  // lifecycle/approval gates. Explode BOM stays a visible primary control
  // for Approved rows (Owner §16). No new actions, no loosened gates.
  const renderWoActions = (r) => {
    const items = [];
    const viewItem = <DropdownMenuItem key="view" data-testid={`view-${r.id}`} onClick={() => openView(r)}>View</DropdownMenuItem>;
    const printItem = <DropdownMenuItem key="print" data-testid={`print-${r.id}`} onClick={() => printPdf(r)}>Print / PDF</DropdownMenuItem>;
    const histItem = <DropdownMenuItem key="hist" data-testid={`history-${r.id}`} onClick={() => setHistoryWo(r)}>History{(r.amendments?.length || 0) > 0 ? ` (${r.amendments.length})` : ""}</DropdownMenuItem>;
    let explodeBtn = null;
    if (r.status === "Draft" && isWoNotSubmitted(r)) {
      items.push(viewItem);
      items.push(<DropdownMenuItem key="edit" data-testid={`edit-${r.id}`} onClick={() => openEdit(r)}>Edit</DropdownMenuItem>);
      if (canSubmitDoc("WO")) items.push(<DropdownMenuItem key="submit" data-testid={`wo-submit-${r.po_no}`} className="text-sky-300" onClick={() => submitForApproval(r)}>Submit for Authorisation</DropdownMenuItem>);
      items.push(printItem, histItem);
      items.push(<DropdownMenuItem key="cancel" data-testid={`wo-cancel-${r.id}`} className="text-red-400" onClick={() => cancel(r)}>Cancel</DropdownMenuItem>);
    } else if (r.status === "Draft" && isWoPending(r)) {
      items.push(viewItem);
      // OD-13 · WO-screen Authorise / Reject (each backend route re-checks its own permission + lifecycle).
      if (hasPerm(null, "production_orders:approve")) items.push(<DropdownMenuItem key="approve" data-testid={`wo-approve-${r.po_no}`} className="text-emerald-300" onClick={() => approve(r)}>Authorise</DropdownMenuItem>);
      if (hasPerm(null, "production_orders:reject")) items.push(<DropdownMenuItem key="reject" data-testid={`wo-reject-${r.po_no}`} className="text-red-400" onClick={() => setRejectWo(r)}>Reject</DropdownMenuItem>);
      items.push(printItem, histItem);
    } else if (r.status === "Draft" && isWoRejected(r)) {
      items.push(viewItem);
      items.push(<DropdownMenuItem key="edit" data-testid={`edit-${r.id}`} onClick={() => openEdit(r)}>Edit / Correct</DropdownMenuItem>);
      if (canSubmitDoc("WO")) items.push(<DropdownMenuItem key="resub" data-testid={`wo-resubmit-${r.po_no}`} className="text-sky-300" onClick={() => submitForApproval(r)}>Resubmit for Authorisation</DropdownMenuItem>);
      items.push(printItem, histItem);
      items.push(<DropdownMenuItem key="cancel" data-testid={`wo-cancel-${r.id}`} className="text-red-400" onClick={() => cancel(r)}>Cancel</DropdownMenuItem>);
    } else if (r.status === "Approved") {
      explodeBtn = (
        <button data-testid={`explode-${r.id}`} onClick={() => openExplode(r)} className="inline-flex items-center gap-1 px-2 py-1 rounded-sm border border-sky-500/40 text-sky-300 hover:bg-sky-500/10 text-[10px] font-mono uppercase tracking-widest">
          <TreeStructureIcon size={12} weight="bold" />Explode BOM
        </button>
      );
      items.push(viewItem);
      items.push(<DropdownMenuItem key="amend" data-testid={`amend-${r.id}`} onClick={() => amend(r)}>Amend</DropdownMenuItem>);
      items.push(printItem, histItem);
    } else if (r.status === "Cancelled") {
      items.push(viewItem, printItem, histItem);
    }
    return (
      <div className="flex items-center justify-end gap-1.5">
        {explodeBtn}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button data-testid={`wo-actions-${r.po_no}`} className="h-7 px-2.5 rounded-sm border border-[var(--border)] text-zinc-300 text-[11px] hover:bg-white/10 whitespace-nowrap">Actions ▾</button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="bg-[var(--bg-1)] border-[var(--border)] text-sm min-w-[180px]">
            {items}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    );
  };

  const WO_COLS = {
    wo_no: { label: "WO No", align: "left", sticky: true, render: (r) => (
      <button onClick={() => openEdit(r)} className="font-mono text-white hover:text-[var(--accent)] whitespace-nowrap" data-testid={`wo-doc-${r.po_no}`}>{r.po_no}</button>
    ) },
    date: { label: "Date", align: "left", render: (r) => <span className="font-mono text-zinc-400 whitespace-nowrap">{r.date}</span> },
    src: { label: "Src", align: "left", render: (r) => <span className="text-[10px] font-mono uppercase tracking-widest px-1.5 py-0.5 rounded-sm border border-[var(--border)] text-zinc-300">{r.source_type}</span> },
    source_order: { label: "SO / ESO No", align: "left", render: (r) => <span className="font-mono text-zinc-400 whitespace-nowrap">{r.source_order_no}</span> },
    item_no: { label: "Item No", align: "left", width: "w-16", render: (r) => <span className="font-mono text-zinc-500 tabular-nums" data-testid={`wo-item-no-${r.id}`}>{woItemNo(r)}</span> },
    style_no: { label: "Style No", align: "left", width: "w-28", render: (r) => { const s = woStyleOf(r); return <span className="font-mono text-zinc-400" data-testid={`wo-style-no-${r.id}`}>{s || "—"}</span>; } },
    product_code: { label: "Product Code", align: "left", render: (r) => <span className="font-mono text-[var(--accent)] block max-w-[260px] line-clamp-2 break-words" title={r.item_code || ""} data-testid={`wo-product-code-${r.id}`}>{r.item_code || "—"}</span> },
    ordered: { label: "Ordered", align: "right", render: (r) => <span className="font-mono tabular-nums text-zinc-300">{r.ordered_qty}</span> },
    buffer: { label: "Buffer", align: "right", render: (r) => <span className="font-mono tabular-nums text-zinc-400" data-testid={`wo-buffer-${r.id}`}>{fmtBuf(r.rejection_allowance_qty)}</span> },
    prod_qty: { label: "Prod Qty", align: "right", render: (r) => <span className="font-mono tabular-nums text-emerald-300" data-testid={`wo-prodqty-${r.id}`}>{r.production_qty}</span> },
    produced: { label: "Produced", align: "right", render: (r) => { const p = woProduced(r); return <span className={`font-mono tabular-nums ${p > 0 ? "text-emerald-300" : "text-zinc-500"}`} data-testid={`wo-produced-${r.id}`}>{p > 0 ? Number(p).toLocaleString("en-IN", { maximumFractionDigits: 0 }) : "0"}</span>; } },
    pending: { label: "Pending", align: "right", render: (r) => { const pend = Math.max(0, Number(r.production_qty || 0) - woProduced(r)); return <span className="font-mono tabular-nums text-amber-300" data-testid={`wo-pending-${r.id}`}>{pend > 0 ? Number(pend).toLocaleString("en-IN", { maximumFractionDigits: 0 }) : "—"}</span>; } },
    target_date: { label: "Target Date", align: "left", render: (r) => {
      const target = r.delivery_date || "";
      const isComplete = ["completed", "closed", "complete"].includes((r.status || "").toLowerCase());
      const isOverdue = !!target && target < woTodayISO && !isComplete;
      return (
        <span className={`font-mono tabular-nums whitespace-nowrap ${isOverdue ? "text-red-400 font-semibold" : "text-zinc-400"}`} title={target ? (isOverdue ? `Overdue · target was ${target}` : `Target completion: ${target}`) : "No target set"} data-testid={`wo-target-${r.id}`}>
          {target || "—"}
          {isOverdue && <span className="ml-1.5 inline-block w-1.5 h-1.5 rounded-full bg-red-500 align-middle" title="Overdue" />}
        </span>
      );
    } },
    status: { label: "Status", align: "left", render: (r) => {
      // Two-tier: lifecycle PRIMARY badge + authorization SECONDARY muted line.
      let secLabel = "";
      if (isWoPending(r)) secLabel = "Waiting for Auth";
      else if (isWoRejected(r)) secLabel = "Rejected";
      else if (isWoAuthorised(r)) secLabel = "Authorised";
      else if (isWoNotSubmitted(r)) secLabel = "Not Submitted";
      return (
        <div className="leading-tight" data-testid={`wo-status-${r.po_no}`}>
          <div className="flex items-center gap-1.5 flex-wrap"><span className={`text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 rounded-sm border ${STATUS_TONE[woLife(r)] || ""}`} data-testid={`wo-status-primary-${r.po_no}`}>{woLife(r)}</span><OnHoldBadge doc={r} testId={`wo-on-hold-${r.po_no}`} /></div>
          {secLabel && (
            <span className={`block mt-1 text-[10px] font-mono tracking-wide ${WO_APPROVAL_SEC_TONE[secLabel] || "text-zinc-500"}`} title={isWoRejected(r) ? (r.rejection_comments || "") : undefined} data-testid={`wo-status-sec-${r.po_no}`}>{secLabel}</span>
          )}
        </div>
      );
    } },
    allocation: { label: "Allocation", align: "left", render: (r) => {
      const alloc = allocationFor(r);
      return (
        <span className={`text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 rounded-sm border ${ALLOC_TONE[alloc.state]}`} data-testid={`wo-alloc-${r.id}`}
          title={alloc.state === "Unallocated" ? "No looms assigned yet — schedule this WO on a machine from Planning" : alloc.state === "Running" ? `${alloc.looms.length} loom${alloc.looms.length === 1 ? "" : "s"} · one currently running production entries` : `Allocated ${alloc.allocated_qty.toFixed(0)} of ${alloc.required.toFixed(0)} m across ${alloc.looms.length} loom${alloc.looms.length === 1 ? "" : "s"}`}>
          {ALLOC_LABEL[alloc.state]}
        </span>
      );
    } },
    mrp: { label: "MRP", align: "left", render: (r) => (
      <span className={`text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 rounded-sm border ${MRP_TONE[r.mrp_status] || MRP_TONE.PENDING}`} data-testid={`wo-mrp-${r.id}`} title={r.mrp_no ? `${r.mrp_no} · ${r.mrp_status}` : "MRP not yet run for this WO"}>
        {r.mrp_status === "EXECUTED" ? "Executed" : r.mrp_status === "CALCULATED" ? "Calculated" : "Pending"}
      </span>
    ) },
    bom: { label: "BOM", align: "left", render: (r) => (
      <span className={`text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 rounded-sm border ${BOM_TONE[r.bom_status] || BOM_TONE.PENDING}`} data-testid={`wo-bom-${r.id}`} title={r.bom_status === "EXPLODED" ? `${r.cwo_count || 0} Child WO${(r.cwo_count || 0) === 1 ? "" : "s"} generated` : "BOM Explosion not yet run for this WO"}>
        {r.bom_status === "EXPLODED" ? `Exploded · ${r.cwo_count || 0}` : "Pending"}
      </span>
    ) },
    // ── optional columns ──
    priority: { label: "Priority", align: "left", render: (r) => <span className="font-mono text-zinc-400" data-testid={`wo-priority-${r.id}`}>{r.priority || "—"}</span> },
    looms: { label: "Assigned Loom(s)", align: "left", render: (r) => {
      const alloc = allocationFor(r);
      if (!alloc.looms.length) return <span className="text-[10px] font-mono uppercase tracking-widest text-zinc-600">—</span>;
      return (
        <div className="flex flex-wrap gap-1 max-w-[220px]" data-testid={`wo-looms-${r.id}`}>
          {alloc.looms.map(l => (
            <button key={l.id} data-testid={`wo-loom-pill-${r.id}-${l.code}`} title={`Open Live Production · ${l.code}${l.status ? ` · ${l.status}` : ""}`} onClick={() => navigate(`/live?machine=${encodeURIComponent(l.code)}`)}
              className={`text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 rounded-sm border transition ${l.status === "Running" ? "border-sky-500/50 text-sky-300 bg-sky-500/10 hover:bg-sky-500/20" : "border-zinc-500/40 text-zinc-300 hover:bg-[var(--bg-2)] hover:border-[var(--accent)] hover:text-[var(--accent)]"}`}>
              {l.code}
            </button>
          ))}
        </div>
      );
    } },
    produced_pct: { label: "Produced %", align: "right", render: (r) => { const req = Number(r.production_qty || 0); const pct = req > 0 ? Math.min(100, Math.round(woProduced(r) / req * 100)) : 0; return <span className="font-mono tabular-nums text-zinc-400" data-testid={`wo-producedpct-${r.id}`}>{req > 0 ? `${pct}%` : "—"}</span>; } },
    mrp_no: { label: "MRP No", align: "left", render: (r) => <span className="font-mono text-zinc-400 whitespace-nowrap">{r.mrp_no || "—"}</span> },
    cwo_count: { label: "CWO Count", align: "right", render: (r) => <span className="font-mono tabular-nums text-zinc-400">{r.cwo_count || 0}</span> },
    remarks: { label: "Remarks", align: "left", render: (r) => <span className="text-zinc-400 block max-w-[220px] line-clamp-2 break-words" title={r.remarks || ""}>{r.remarks || "—"}</span> },
    actions: { label: "Actions", align: "right", width: "w-32 min-w-[128px]", render: (r) => renderWoActions(r) },
  };

  return (
    <div className="p-6 space-y-5">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="overline mb-1">Production</div>
          <h1 className="font-display text-3xl font-black tracking-tight text-white">Work Orders</h1>
          <div className="text-xs text-zinc-500 mt-1">Generate a Work Order from a Sales Order or Export Sales Order · plans rejection allowance to arrive at Production Qty</div>
        </div>
        <div className="flex items-center gap-2 print-hide">
          {(() => {
            const submitable = filtered.filter(r => isWoNotSubmitted(r) || isWoRejected(r));
            return submitable.length > 0 && (
              <button data-testid="bulk-approve-btn" onClick={bulkApprove} title="Submit every Not-Submitted / Rejected Work Order matching the current filter for Management Authorisation"
                className="h-10 px-4 border border-sky-500/50 text-sky-300 hover:bg-sky-500/10 text-sm font-semibold rounded-sm flex items-center gap-2">
                <PaperPlaneTiltIcon size={14} weight="bold" />Submit All for Approval <span className="font-mono text-[10px] opacity-70">({submitable.length})</span>
              </button>
            );
          })()}
          {filtered.filter(r => r.status === "Approved").length > 0 && (
            <button data-testid="bulk-explode-btn" onClick={bulkExplode} title="Explode BOM + generate Child WOs for every Approved Work Order without Child WOs matching the current filter"
              className="h-10 px-4 border border-sky-500/50 text-sky-300 hover:bg-sky-500/10 text-sm font-semibold rounded-sm flex items-center gap-2">
              <TreeStructureIcon size={14} weight="bold" />Explode All Approved <span className="font-mono text-[10px] opacity-70">({filtered.filter(r => r.status === "Approved").length})</span>
            </button>
          )}
          <button data-testid="new-po-btn" onClick={openNew} className="h-10 px-4 bg-[var(--accent)] hover:bg-blue-600 text-white text-sm font-semibold rounded-sm flex items-center gap-2">
            <PlusIcon size={14} weight="bold" />New Work Order
          </button>
          {/* Feb 2026 · Print / PDF the full WO Status list — uses the
              browser's native print pipeline so operators can Save-as-PDF
              or send straight to a physical printer. `@media print`
              styles below strip the side nav + toolbar. */}
          <button
            data-testid="wo-print-pdf-btn"
            onClick={() => {
              document.body.classList.add("print-wo-status");
              setTimeout(() => {
                window.print();
                setTimeout(() => document.body.classList.remove("print-wo-status"), 500);
              }, 100);
            }}
            title="Print or Save-as-PDF the current Work Order Status list (respects the active filter)"
            className="h-10 px-4 border border-[var(--border)] hover:bg-[var(--bg-2)] text-white text-sm font-semibold rounded-sm flex items-center gap-2"
          >
            <PrinterIcon size={14} weight="bold" />Print / PDF
          </button>
        </div>
      </div>

      {/* KPI strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 print-hide">
        <Kpi label="Draft" value={kpis.draft} tone="zinc" testid="kpi-draft" />
        <Kpi label="Approved" value={kpis.approved} tone="emerald" testid="kpi-approved" />
        <Kpi label="Cancelled" value={kpis.cancelled} tone="red" testid="kpi-cancelled" />
        <Kpi label="Total Planned Qty" value={Math.round(kpis.totalPlanQty)} suffix="units" tone="blue" testid="kpi-total-qty" />
      </div>

      {/* Filter chips */}
      <div className="flex items-center gap-2 text-[11px] print-hide">
        <span className="text-zinc-500 font-mono uppercase tracking-widest">Filter:</span>
        {["all", "Draft", "Approved", "Cancelled", "Hold"].map(s => (
          <button key={s} data-testid={`filter-${s}`} onClick={() => setFilterStatus(s)}
            className={`px-2.5 py-1 rounded-sm border font-mono uppercase tracking-widest ${
              filterStatus === s ? "border-[var(--accent)] bg-[var(--accent)]/10 text-white"
              : "border-[var(--border)] text-zinc-500 hover:text-white"
            }`}>
            {s === "all" ? `All (${rows.length})` : s === "Hold" ? `Hold (${rows.filter(r => r.on_hold === true).length})` : s}
          </button>
        ))}
      </div>

      {/* WO Register V1 · search · density · columns toolbar */}
      <div className="flex flex-wrap items-center gap-2 print-hide">
        <div className="relative w-full max-w-md">
          <MagnifyingGlassIcon size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
          <input
            data-testid="wo-search"
            value={woSearch}
            onChange={e => setWoSearch(e.target.value)}
            placeholder="Search WO no, SO/ESO, item no, style, product code, status…"
            className="w-full h-9 pl-9 pr-3 bg-[var(--bg-0)] border border-[var(--border)] rounded-sm text-sm placeholder:text-zinc-500 focus:outline-none focus:border-[var(--accent)]"
          />
        </div>
        {woSearch && (
          <button data-testid="wo-search-clear" onClick={() => setWoSearch("")} className="h-9 px-3 text-xs text-zinc-400 hover:text-white border border-[var(--border)] rounded-sm">Clear</button>
        )}
        <div className="flex-1" />
        <div className="inline-flex rounded-sm border border-[var(--border)] overflow-hidden" role="group" aria-label="Row density">
          {[["comfortable", "Comfy"], ["compact", "Compact"]].map(([k, l]) => (
            <button key={k} data-testid={`wo-density-${k}`} onClick={() => setWoDensityPersist(k)}
              className={`text-[10px] font-mono uppercase tracking-widest px-2.5 py-1.5 ${woDensity === k ? "bg-[var(--accent)]/15 text-[var(--accent)]" : "text-zinc-400 hover:text-zinc-200"}`}>{l}</button>
          ))}
        </div>
        <div className="relative">
          <button data-testid="wo-cols-btn" onClick={() => setWoShowColsMenu(v => !v)}
            className="h-[30px] text-[11px] font-mono uppercase tracking-widest px-3 border border-[var(--border)] hover:border-[var(--accent)] rounded-sm text-zinc-300 flex items-center gap-2" title="Show / hide columns">
            <span className="font-mono">{woVisCols.length}/{WO_COL_ORDER.length}</span> Columns
          </button>
          {woShowColsMenu && (
            <>
              <div className="fixed inset-0 z-30" onClick={() => setWoShowColsMenu(false)} />
              <div className="absolute right-0 top-9 z-40 w-64 bg-[var(--bg-1)] border border-[var(--border)] rounded-sm shadow-xl p-2 max-h-96 overflow-y-auto" data-testid="wo-cols-menu">
                <div className="flex items-center justify-between px-1 pb-2 border-b border-[var(--border)] mb-2">
                  <div className="text-[10px] uppercase tracking-widest text-zinc-500">Show columns</div>
                  <button onClick={woResetCols} className="text-[10px] font-mono uppercase tracking-widest text-[var(--accent)] hover:underline" data-testid="wo-cols-reset">Reset</button>
                </div>
                {WO_COL_ORDER.map(key => {
                  const c = WO_COLS[key];
                  if (!c) return null;
                  const pinned = WO_PINNED_COLS.has(key);
                  return (
                    <label key={key} data-testid={`wo-col-opt-${key}`} className={`flex items-center gap-2 px-2 py-1 rounded-sm text-xs hover:bg-[var(--bg-2)] cursor-pointer ${pinned ? "opacity-60" : ""}`}>
                      <input type="checkbox" checked={woVisibleCols.has(key) || pinned} disabled={pinned} onChange={() => toggleWoCol(key)} className="w-3.5 h-3.5 accent-[var(--accent)]" />
                      <span className="text-zinc-200 flex-1">{c.label}</span>
                      {pinned && <span className="text-[9px] font-mono uppercase tracking-widest text-zinc-500">pinned</span>}
                    </label>
                  );
                })}
              </div>
            </>
          )}
        </div>
      </div>

      {/* List */}
      <div className="tactical-card overflow-x-auto max-h-[calc(100vh-300px)] overflow-y-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-xs uppercase tracking-widest text-zinc-400 border-b border-[var(--border)] sticky top-0 z-30 bg-[var(--bg-1)] shadow-[0_1px_0_var(--border)]">
              {woVisCols.map(key => {
                const col = WO_COLS[key];
                if (!col) return null;
                return (
                  <th key={key} data-testid={`wo-col-${key}`}
                    className={`${woCellPad} font-semibold select-none ${col.align === "right" ? "text-right" : "text-left"} ${col.width || ""} ${col.sticky ? "sticky left-0 z-30 bg-[var(--bg-1)] shadow-[1px_0_0_var(--border)]" : ""}`}>
                    {col.label}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {woDisplayed.map(r => (
              <tr key={r.id} data-testid={`row-${r.id}`}
                className={`border-b border-[var(--border)] hover:bg-[var(--bg-2)]/40 ${r.status === "Cancelled" ? "[&>td]:text-zinc-500 opacity-75" : ""}`}>
                {woVisCols.map(key => {
                  const col = WO_COLS[key];
                  if (!col) return null;
                  return (
                    <td key={key}
                      className={`${woCellPad} ${col.align === "right" ? "text-right" : ""} ${col.width || ""} ${col.sticky ? "sticky left-0 z-20 bg-[var(--bg-1)] shadow-[1px_0_0_var(--border)]" : ""}`}>
                      {col.render(r)}
                    </td>
                  );
                })}
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr><td colSpan={woVisCols.length} className="text-center py-16 text-zinc-500">No work orders {filterStatus !== "all" ? `in ${filterStatus}` : "yet"}</td></tr>
            )}
            {filtered.length > 0 && woDisplayed.length === 0 && (
              <tr><td colSpan={woVisCols.length} className="text-center py-10 text-zinc-500">No work orders match "{woSearch}"</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[var(--bg-1)] border-[var(--border)] text-white max-w-5xl max-h-[92vh] overflow-y-auto" description="Work Order action — targets, machines, material issue and print.">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-3">
              {editId ? (viewOnly ? "View Work Order" : "Edit Work Order") : "New Work Order"}
              {form.po_no && <span className="text-xs font-mono text-zinc-400">{form.po_no}</span>}
              <span className={`text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 rounded-sm border ${STATUS_TONE[woLife(form)] || ""}`} data-testid="wo-view-status">{woLife(form)}</span>
              {isWoAuthorised(form) && woLife(form) !== form.status && (
                <span data-testid={`wo-preview-authorised-${form.po_no}`} className={`text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 rounded-sm border ${WO_APPROVAL_TONE["Approved"]}`}>Authorised</span>
              )}
              {/* P1-WO Phase 2C · Path B · Feb 2026 — dialog title
                  mirrors the row's Management Authorisation state so
                  planners see the same badge in View mode. */}
              {isWoPending(form) && (
                <span data-testid={`wo-preview-pending-${form.po_no}`} className={`text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 rounded-sm border ${WO_APPROVAL_TONE["Pending Approval"]}`}>Waiting for Auth</span>
              )}
              {isWoRejected(form) && (
                <span data-testid={`wo-preview-rejected-${form.po_no}`} title={form.rejection_comments || ""} className={`text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 rounded-sm border ${WO_APPROVAL_TONE["Rejected"]}`}>Rejected</span>
              )}
              {isWoNotSubmitted(form) && form.po_no && (
                <span data-testid={`wo-preview-notsubmitted-${form.po_no}`} className={`text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 rounded-sm border ${WO_APPROVAL_TONE["Not Submitted"]}`}>Not Submitted</span>
              )}
              {viewOnly && <OnHoldBadge doc={form} testId="wo-view-on-hold" />}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            {/* Phase 2C-6B3A · Cancelled Work Order audit banner (View). */}
            {form?.status === "Cancelled" && (
              <div data-testid="wo-view-cancelled-banner" className="rounded-sm border border-red-500/40 bg-red-500/10 px-4 py-3 text-sm">
                <div className="flex items-center gap-2 text-red-300 font-semibold">
                  <ProhibitIcon size={16} weight="bold" /> This Work Order is Cancelled
                </div>
                {form?.cancellation_reason && (
                  <div className="mt-1 text-zinc-300" data-testid="wo-view-cancel-reason">
                    <span className="text-zinc-500">Reason:</span> {form.cancellation_reason}
                  </div>
                )}
                <div className="mt-0.5 text-xs text-zinc-500 flex flex-wrap gap-x-4">
                  {form?.cancelled_by && <span data-testid="wo-view-cancelled-by">Cancelled By: {form.cancelled_by}</span>}
                  {form?.cancelled_at && <span data-testid="wo-view-cancelled-at">Cancelled At: {form.cancelled_at}</span>}
                </div>
              </div>
            )}
            {/* Source */}
            <div className="border border-[var(--border)] rounded-sm bg-[var(--bg-0)]/40 p-3">
              <div className="text-[11px] font-mono uppercase tracking-widest text-zinc-400 mb-3">Source</div>
              <div className="grid grid-cols-4 gap-3">
                <F label="Date"><input type="date" data-testid="po-date" value={form.date} onChange={e => setForm({ ...form, date: e.target.value })} className={inp} /></F>
                <F label="Source Type">
                  <div className="flex gap-1 h-9">
                    {["SO", "ESO"].map(t => (
                      <button key={t} type="button" data-testid={`src-${t}`} onClick={() => onSwitchSource(t)}
                        className={`flex-1 rounded-sm text-xs font-mono uppercase tracking-widest border ${
                          form.source_type === t
                            ? "border-[var(--accent)] bg-[var(--accent)]/10 text-white"
                            : "border-[var(--border)] text-zinc-400 hover:text-white"
                        }`}>{t}</button>
                    ))}
                  </div>
                </F>
                <div className="col-span-2">
                  <F label={form.source_type === "ESO" ? "Export Sales Order *" : "Sales Order *"}>
                    <Select value={form.source_order_id} onValueChange={onPickOrder}>
                      <SelectTrigger data-testid="po-source-order" className={selCls}><SelectValue placeholder="Pick an order" /></SelectTrigger>
                      <SelectContent className="bg-[var(--bg-1)] border-[var(--border)] max-h-64">
                        {sourceOptions.map(o => {
                          const locked = isOrderFullyLocked(o);
                          return (
                            <SelectItem key={o.id} value={o.id} disabled={locked} data-testid={`wo-source-option-${o.order_no}`}>
                              {o.order_no}{isStockOrder(o) ? " · STOCK" : ""}{locked ? "  · linked" : ""}
                            </SelectItem>
                          );
                        })}
                      </SelectContent>
                    </Select>
                  </F>
                </div>
                {/* Feb 2026 · Product Code picker is now optional — the
                    line-items grid below drives Production Qty. This picker
                    still marks the "primary" line for the header snapshot
                    (customer / delivery date / SO status). Auto-set to the
                    first line when the SO is picked. */}
                <div className="col-span-4">
                  <F label="Primary Product Code (drives header snapshot)">
                    <Select value={form.source_line_id} onValueChange={onPickLine} disabled={!form.source_order_id || viewOnly}>
                      <SelectTrigger data-testid="po-product-code" className={selCls}><SelectValue placeholder={form.source_order_id ? "Auto-picked from first line" : "Pick order first"} /></SelectTrigger>
                      <SelectContent className="bg-[var(--bg-1)] border-[var(--border)] max-h-64">
                        {currentLines.map((li, i) => {
                          const code = items.find(x => x.id === li.item_id)?.description
                                    || items.find(x => x.id === li.item_id)?.code
                                    || li.article_no || `Line ${i+1}`;
                          const lid = li.line_id || `idx-${i}`;
                          const linked = lockedPoFor(form.source_order_id, lid);
                          return (
                            <SelectItem key={lid} value={lid} disabled={!!linked}>
                              {code}{linked ? `  · linked to ${linked.po_no}` : ""}
                            </SelectItem>
                          );
                        })}
                      </SelectContent>
                    </Select>
                  </F>
                  {/* Feb 27, 2026 · Balance-Mismatch banner — flags cancelled /
                      short-closed source lines and offers a one-click "Snap
                      qty to available" that rewrites every line's order_qty
                      down to the un-cancelled balance. Reads snapshot fields
                      populated by production_orders._load_source_line. */}
                  {form.balance_mismatch && (
                    <div data-testid="wo-balance-mismatch" className="mt-3 rounded-sm border border-amber-500/50 bg-amber-500/10 p-3">
                      <div className="flex items-start gap-3">
                        <WarningIcon size={18} weight="fill" className="text-amber-400 mt-0.5 shrink-0" />
                        <div className="flex-1">
                          <div className="text-[11px] font-mono uppercase tracking-widest text-amber-300">Balance Mismatch · Source Line Cancelled</div>
                          <div className="mt-1 text-xs text-zinc-200 leading-relaxed">
                            {form.balance_mismatch_reason || `Source line has ${fmtG(form.line_cancelled_qty || 0)} qty cancelled — Work Order qty may need adjustment.`}
                          </div>
                          <div className="mt-2 flex items-center gap-4 text-[11px] font-mono">
                            <div><span className="text-zinc-500 uppercase tracking-widest">Ordered</span>&nbsp;<span className="tabular-nums text-zinc-200">{fmtG(form.ordered_qty || 0)}</span></div>
                            <div><span className="text-zinc-500 uppercase tracking-widest">Cancelled</span>&nbsp;<span className="tabular-nums text-red-300">{fmtG(form.line_cancelled_qty || 0)}</span></div>
                            <div><span className="text-zinc-500 uppercase tracking-widest">Available</span>&nbsp;<span className="tabular-nums text-emerald-300 font-bold">{fmtG(form.available_qty || 0)}</span></div>
                          </div>
                        </div>
                        {!viewOnly && (form.available_qty || 0) < (form.ordered_qty || 0) && (
                          <button
                            type="button"
                            data-testid="wo-snap-qty"
                            onClick={() => {
                              const av = Number(form.available_qty || 0);
                              setForm(f => {
                                // Rewrite header + every matching line to the un-cancelled available qty.
                                const patched = (f.items || []).map(li => {
                                  if ((li.line_id || "") !== f.source_line_id && !((f.source_line_id || "").startsWith("idx-") && f.items.indexOf(li) === parseInt(f.source_line_id.split("-")[1] || "0", 10))) {
                                    return li;
                                  }
                                  return { ...li, order_qty: av, total_qty: av + (Number(li.extra_qty) || 0) };
                                });
                                return { ...f, ordered_qty: av, items: patched };
                              });
                              toast.success(`Snapped WO qty to ${av.toFixed(2)} (available after cancellation)`);
                            }}
                            className="shrink-0 h-8 px-3 text-[10px] font-mono uppercase tracking-widest rounded-sm bg-amber-500 hover:bg-amber-600 text-white transition"
                          >
                            Snap qty to available
                          </button>
                        )}
                      </div>
                    </div>
                  )}
                  {/* Feb 2026 · Departmental sequence auto-identified from
                      the picked product's Production Routing (Path A · B · C
                      on Article Master). Empty state prompts the operator
                      to set it on the master. */}
                  {routingHint && routingHint.path ? (
                    <div className="mt-3 rounded-sm border border-[var(--border)] bg-[var(--bg-2)]/40 p-2.5" data-testid="wo-routing-chip-strip">
                      <div className="flex items-center gap-2 flex-wrap text-[10px] font-mono uppercase tracking-widest">
                        <span className="text-zinc-500">Routing</span>
                        <span className={`px-1.5 py-0.5 rounded-sm border ${
                          routingHint.path.yarn_kind_hint === "GREY"
                            ? "border-amber-500/40 text-amber-300 bg-amber-500/10"
                            : "border-fuchsia-500/40 text-fuchsia-300 bg-fuchsia-500/10"
                        }`}>{routingHint.path.code}</span>
                        <span className="text-zinc-400 normal-case tracking-normal">{routingHint.path.label}</span>
                      </div>
                      <div className="mt-2 flex items-center gap-1 flex-wrap">
                        {routingHint.path.stages.map((s, i) => (
                          <span key={s.code} className="inline-flex items-center gap-1" data-testid={`wo-routing-stage-${s.code}`}>
                            <span className="text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 rounded-sm border border-sky-500/40 text-sky-300 bg-sky-500/10">
                              {s.name}
                            </span>
                            {i < routingHint.path.stages.length - 1 && <span className="text-zinc-600">→</span>}
                          </span>
                        ))}
                        <span className={`ml-auto text-[9px] font-mono uppercase tracking-widest px-1.5 py-0.5 rounded-sm border ${
                          routingHint.path.yarn_kind_hint === "GREY"
                            ? "border-amber-500/40 text-amber-300 bg-amber-500/10"
                            : "border-fuchsia-500/40 text-fuchsia-300 bg-fuchsia-500/10"
                        }`}>
                          Suggested Yarn · {routingHint.path.yarn_kind_hint}
                        </span>
                      </div>
                    </div>
                  ) : (routingHint && !routingHint.path && form.source_line_id ? (
                    <div className="mt-3 rounded-sm border border-dashed border-amber-500/40 bg-amber-500/5 p-2 text-[10px] text-amber-200" data-testid="wo-routing-unset">
                      Production Routing not set on Article Master for <span className="font-mono">{routingHint.product_code}</span> — set Path A · B · C to auto-identify the sequence.
                    </div>
                  ) : null)}
                  {/* Feb 2026 · Post-Production Job Card generator —
                      instruction-only cards for Dyeing/Finishing + Checking &
                      Packing. Gated by Weaving milestone status. */}
                  {postProdElig && editId && (
                    <div className="mt-3 rounded-sm border border-[var(--border)] bg-[var(--bg-2)]/40 p-2.5" data-testid="wo-postprod-panel">
                      <div className="flex items-center justify-between mb-2">
                        <div className="overline text-[var(--accent)]">Generate Instructions · Post-Production Job Cards</div>
                        <span className={`text-[9px] font-mono uppercase tracking-widest px-1.5 py-0.5 rounded-sm border ${
                          postProdElig.allow
                            ? "border-emerald-500/40 text-emerald-300 bg-emerald-500/10"
                            : "border-zinc-500/40 text-zinc-500 bg-zinc-500/10"
                        }`}>Weaving · {postProdElig.weaving_status}</span>
                      </div>
                      {!postProdElig.allow && (
                        <div className="text-[10px] text-zinc-500 mb-2">
                          Post-production Job Cards unlock once Weaving is <span className="text-emerald-300 font-mono">In-Process</span> or <span className="text-emerald-300 font-mono">Completed</span>.
                        </div>
                      )}
                      <div className="flex items-center gap-2 flex-wrap">
                        {(postProdElig.stages || []).map(s => (
                          <button
                            key={s.stage_code}
                            data-testid={`wo-postprod-gen-${s.stage_code}`}
                            disabled={!postProdElig.allow || s.already_generated}
                            onClick={() => genPostProdJC(s.stage_code)}
                            className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-sm border text-[10px] font-mono uppercase tracking-widest transition ${
                              s.already_generated
                                ? "border-emerald-500/40 text-emerald-300 bg-emerald-500/5 cursor-default"
                                : postProdElig.allow
                                  ? "border-sky-500/40 text-sky-300 bg-sky-500/10 hover:bg-sky-500/20"
                                  : "border-zinc-700 text-zinc-600 bg-zinc-800/40 cursor-not-allowed"
                            }`}
                          >
                            {s.already_generated ? "✓ " : "+ "}{s.label}
                            {s.already_generated && s.job_card_no && (
                              <span className="normal-case tracking-normal text-emerald-400/80">· {s.job_card_no}</span>
                            )}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
              {/* Auto-loaded lines table — SO-parity grid (Feb 2026, user
                  request). One row per SO/ESO line with editable per-line
                  Rejection Allowance % + Extra Qty. Rate/Value columns from
                  SO are intentionally omitted since Work Orders don't carry
                  commercial data. Totals row aggregates so Production Qty
                  reflects the sum of all lines. */}
              {form.items && form.items.length > 0 && (
                <div className="mt-4">
                  <div className="text-[11px] font-mono uppercase tracking-widest text-zinc-400 mb-2">
                    Line Items ({form.items.length}) · edit per-line Rejection Allowance
                  </div>
                  <div className="border border-[var(--border)] rounded-sm overflow-x-auto">
                    <table className="w-full text-xs">
                      <thead className="bg-[var(--bg-2)]/40">
                        <tr className="text-[10px] uppercase tracking-widest text-zinc-500">
                          <th className="text-left px-2 py-2 font-medium w-14">Item No</th>
                          <th className="text-left px-2 py-2 font-medium">Style No</th>
                          <th className="text-left px-2 py-2 font-medium">Product Code</th>
                          <th className="text-right px-2 py-2 font-medium bg-pink-500/10 w-24">Order Qty</th>
                          <th className="text-right px-2 py-2 font-medium w-24">Rej Allow %</th>
                          <th className="text-right px-2 py-2 font-medium w-24" title="Over-Dispatch Buffer · technical over-production tolerance">Buffer Qty</th>
                          <th className="text-right px-2 py-2 font-medium bg-emerald-500/10 w-24">Total Qty</th>
                          <th className="text-left px-2 py-2 font-medium w-32">Delivery Date</th>
                          <th className="text-left px-2 py-2 font-medium w-16">Unit</th>
                        </tr>
                      </thead>
                      <tbody data-testid="wo-source-lines">
                        {form.items.map((li, i) => (
                          <tr key={li.line_id || i} className={`border-t border-[var(--border)] ${li.line_id === form.source_line_id ? "bg-emerald-500/5" : ""}`}>
                            <td className="px-2 py-1 font-mono text-zinc-500">{String(i + 1).padStart(3, "0")}</td>
                            <td className="px-2 py-1 font-mono text-zinc-300">{li.style_no || "—"}</td>
                            <td className="px-2 py-1 font-mono text-white">
                              {li.product_code || li.item_description || "—"}
                              {li.line_id === form.source_line_id && (
                                <span className="ml-2 text-[9px] font-mono uppercase tracking-widest text-emerald-300">primary</span>
                              )}
                            </td>
                            <td className="px-1 py-1 text-right">
                              <FormattedNumberInput
                                step="0.01"
                                data-testid={`wo-line-order-${i}`}
                                value={li.order_qty}
                                readOnly={!!viewOnly}
                                onChange={e => updateLine(i, "order_qty", parseFloat(e.target.value) || 0)}
                                className="w-full h-7 px-2 text-right bg-[var(--bg-0)] border border-[var(--border)] rounded-sm text-white text-xs font-mono cursor-not-allowed opacity-80"
                                title="Ordered Qty is pulled from the source SO — edit the SO to change it"
                              />
                            </td>
                            <td className="px-1 py-1 text-right">
                              <FormattedNumberInput
                                step="0.01"
                                data-testid={`wo-line-rej-pct-${i}`}
                                value={li.rejection_allowance_pct}
                                readOnly={!!viewOnly}
                                onChange={e => updateLine(i, "rejection_allowance_pct", parseFloat(e.target.value) || 0)}
                                className="w-full h-7 px-2 text-right bg-[var(--bg-0)] border border-[var(--border)] rounded-sm text-white text-xs font-mono focus:outline-none focus:border-[var(--accent)]"
                                title="Per-line Rejection Allowance — bump to cover high-risk products"
                              />
                            </td>
                            <td className="px-1 py-1 text-right">
                              <FormattedNumberInput
                                step="1"
                                data-testid={`wo-line-extra-${i}`}
                                value={li.extra_qty}
                                readOnly={!!viewOnly}
                                onChange={e => updateLine(i, "extra_qty", parseFloat(e.target.value) || 0)}
                                className="w-full h-7 px-2 text-right bg-[var(--bg-0)] border border-[var(--border)] rounded-sm text-white text-xs font-mono focus:outline-none focus:border-[var(--accent)]"
                                title="Overrides the % — direct edit sticks even when % changes"
                              />
                            </td>
                            <td className="px-2 py-1 text-right font-mono text-emerald-300">{(+li.total_qty || 0).toFixed(2)}</td>
                            <td className="px-1 py-1">
                              <input
                                type="date"
                                data-testid={`wo-line-delivery-${i}`}
                                value={li.delivery_date || ""}
                                readOnly={!!viewOnly}
                                onChange={e => updateLine(i, "delivery_date", e.target.value)}
                                className="w-full h-7 px-2 bg-[var(--bg-0)] border border-[var(--border)] rounded-sm text-white text-xs font-mono focus:outline-none focus:border-[var(--accent)]"
                              />
                            </td>
                            <td className="px-2 py-1 font-mono text-zinc-500">{li.unit}</td>
                          </tr>
                        ))}
                        <tr className="border-t border-[var(--border)] bg-[var(--bg-2)]/20">
                          <td colSpan={3} className="px-2 py-2 text-right text-[10px] uppercase tracking-widest text-zinc-500 font-medium">Totals</td>
                          <td className="px-2 py-2 text-right font-mono text-white bg-pink-500/10">
                            {form.items.reduce((s, l) => s + (+l.order_qty || 0), 0).toFixed(2)}
                          </td>
                          <td></td>
                          <td className="px-2 py-2 text-right font-mono text-white">
                            <span data-testid="wo-lines-extra-total">{fmtBuf(form.items.reduce((s, l) => s + (+l.extra_qty || 0), 0))}</span>
                          </td>
                          <td className="px-2 py-2 text-right font-mono text-emerald-300 bg-emerald-500/10">
                            {form.items.reduce((s, l) => s + (+l.total_qty || 0), 0).toFixed(2)}
                          </td>
                          <td colSpan={2}></td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>

            {/* Sales Order Header Snapshot — read-only context lifted from
                the source SO, mirrors the SO entry screen's top header (Feb
                2026 · user request). Per-line data (Product Code, Order Qty,
                Delivery Date) now lives in the Line Items grid above so this
                block only carries order-level fields. */}
            <div className="border border-[var(--border)] rounded-sm bg-[var(--bg-0)]/40 p-3">
              <div className="text-[11px] font-mono uppercase tracking-widest text-zinc-400 mb-3">Sales Order Header (Read Only)</div>
              <div className="grid grid-cols-4 gap-3">
                <RO label="Sales Date" value={form.sales_date} testid="po-sales-date-ro" />
                {/* Customer + Customer PO redacted on production floor · Mar 2026 · Tiered Visibility */}
                <RO label="Delivery Date" value={form.delivery_date} testid="po-delivery-ro" />
                <RO label="Priority" value={form.priority} testid="po-priority-ro" />
                <RO label="SO Status" value={form.so_status} testid="po-so-status-ro" />
                <div className="col-span-4">
                  <RO label="Instructions" value={form.special_instructions} testid="po-instructions-ro" />
                </div>
              </div>
            </div>

            {/* Planner inputs — legacy single-line fallback. Only shown when
                the WO has no line items (edge case: legacy WOs saved before
                the line-wise upgrade). New WOs pick up per-line % from the
                line-items grid above. */}
            {(!form.items || form.items.length === 0) && (
              <div className="border border-[var(--border)] rounded-sm bg-[var(--bg-0)]/40 p-3">
                <div className="text-[11px] font-mono uppercase tracking-widest text-zinc-400 mb-3">Planner Inputs</div>
                <div className="grid grid-cols-4 gap-3">
                  <F label="Rejection Allowance %" hint={`= ${rejAllowanceQty} units`}>
                    <FormattedNumberInput step="0.01" data-testid="po-rej-pct" value={form.rejection_allowance_pct}
                      onChange={e => setForm({ ...form, rejection_allowance_pct: parseFloat(e.target.value) || 0 })} className={inp} />
                  </F>
                </div>
              </div>
            )}

            {/* Compute panel */}
            <div className="border border-emerald-500/30 bg-emerald-500/5 rounded-sm p-3 flex items-center justify-between flex-wrap gap-3">
              <div className="text-[11px] font-mono uppercase tracking-widest text-emerald-300">
                {form.items && form.items.length > 0 ? `Auto Calculated · ${form.items.length} lines` : "Auto Calculated"}
              </div>
              <div className="flex items-center gap-6 font-mono text-sm">
                <span className="text-zinc-400">Ordered</span>
                <span className="text-white">{totalOrdered.toFixed(2)}</span>
                <span className="text-zinc-500">+</span>
                <span className="text-zinc-400">Rej Allow</span>
                <span className="text-white" data-testid="po-buffer-qty">{fmtBuf(rejAllowanceQty)}</span>
                <span className="text-zinc-500">=</span>
                <span className="text-[10px] font-mono uppercase tracking-widest text-zinc-400">Production Qty</span>
                <span data-testid="po-prod-qty" className="text-emerald-300 text-lg font-black">{productionQty.toFixed(2)}</span>
              </div>
            </div>

            <F label="Remarks">
              <input value={form.remarks || ""} onChange={e => setForm({ ...form, remarks: e.target.value })} className={inp} />
            </F>

            {problems.length > 0 && (
              <div data-testid="problems" className="rounded-sm border border-amber-500/40 bg-amber-500/5 px-3 py-2 text-[11px] text-amber-200">
                <div className="font-mono uppercase tracking-widest text-amber-300 mb-1">Blockers before Submit</div>
                <ul className="list-disc list-inside space-y-0.5">
                  {problems.map((p, i) => <li key={i}>{p}</li>)}
                </ul>
              </div>
            )}
          </div>

          <DialogFooter className="gap-2">
            {/* Feb 2026 · Modify Work Order — replaces the legacy Unapprove
                button. Opens the shared ModifyDocumentPopover which
                link-probes Child WOs / Material Reqs and either lets an
                Admin Unapprove (when nothing is linked) or Short Close /
                Cancel Balance per line item (when CWOs already exist). */}
            {viewOnly && form?.status === "Approved" && (getUser()?.role === "Administrator" || hasPerm(null, "production_orders:unapprove")) && (
              <button
                data-testid="wo-modify"
                onClick={() => setModifyOpen(true)}
                className="px-4 py-2 rounded-sm border border-amber-500/50 text-amber-300 hover:bg-amber-500/10 text-sm font-semibold mr-auto"
              >Modify Order</button>
            )}
            <button onClick={() => setOpen(false)} className="px-4 py-2 rounded-sm border border-[var(--border)] text-sm text-zinc-400">{viewOnly ? "Close" : "Cancel"}</button>
            {/* P1-WO Phase 2C · Path B · Feb 2026 — View mode surfaces
                the legitimate next Management Authorisation lifecycle
                action (Submit / Resubmit) via the SAME canonical
                handler used by the row action strip. Owner UI
                Uniformity rule (MA-INV-09) mirrors SO Phase 2B. */}
            {viewOnly && canSubmitDoc("WO") && (isWoNotSubmitted(form) || isWoRejected(form)) && form?.id && (
              <button
                data-testid={`wo-preview-submit-${form.po_no}`}
                onClick={() => submitForApproval(form)}
                className="px-4 py-2 rounded-sm bg-[var(--accent)] hover:bg-blue-600 text-sm font-semibold text-white flex items-center gap-2"
              >
                <PaperPlaneTiltIcon size={14} weight="bold" />
                {isWoRejected(form) ? "Resubmit for Approval" : "Submit for Approval"}
              </button>
            )}
            {viewOnly && (form?.status === "Approved" || form?.on_hold === true) && (
              <HoldReleaseButtons kind="wo" doc={form} onDone={() => { setOpen(false); load(); }} />
            )}
            {viewOnly && <ShortCloseButton doc={form} produced={woProduced(form)} onDone={() => { setOpen(false); load(); }} />}
            {/* Phase 2C-6B3A · Work Order cancellation is now reachable ONLY
                from View mode (removed from the main-row Actions to prevent
                accidental cancellation). Eligible = Approved & not already
                Cancelled & holder of production_orders:cancel. Backend
                re-validates permission + mandatory reason. */}
            {viewOnly && form?.status === "Approved" && form?.id && hasPerm(null, "production_orders:cancel") && (
              <button
                data-testid="wo-view-cancel"
                onClick={() => cancel(form)}
                className="px-4 py-2 rounded-sm border border-red-500/50 text-red-300 hover:bg-red-500/10 text-sm font-semibold flex items-center gap-2"
              >
                <ProhibitIcon size={14} weight="bold" /> Cancel Work Order
              </button>
            )}
            {!viewOnly && (
              <>
                <button data-testid="save-draft" onClick={() => save(false)} disabled={problems.length > 0 && problems[0].startsWith("Pick")}
                  className="px-4 py-2 rounded-sm border border-[var(--border)] text-sm text-zinc-200 hover:border-[var(--accent)] disabled:opacity-40 disabled:cursor-not-allowed">Save Draft</button>
                {/* P1-WO Phase 2C · Path B · Feb 2026 — Save & Submit
                    replaces the retired Save & Approve. Save writes the
                    WO as Not Submitted; the Submit call then enters
                    the Management Authorisation queue explicitly. */}
                {canSubmitDoc("WO") && (<button data-testid="save-submit" onClick={() => save(true)} disabled={problems.length > 0}
                  className="px-4 py-2 rounded-sm bg-[var(--accent)] hover:bg-blue-600 disabled:bg-zinc-700 disabled:cursor-not-allowed text-sm font-semibold text-white">Save &amp; Submit for Approval</button>)}
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Phase 2C-6B3A · controlled Work Order cancellation dialog —
          mandatory reason + explicit danger confirmation. */}
      <Dialog open={!!cancelPrompt} onOpenChange={(o) => { if (!o) { setCancelPrompt(null); setCancelReason(""); } }}>
        <DialogContent className="bg-[var(--bg-1)] border-[var(--border)] text-white max-w-md" description="Cancel a Work Order with a mandatory reason.">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-300">
              <ProhibitIcon size={18} weight="bold" /> Cancel Work Order
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-1">
            <div className="text-sm text-zinc-400">
              Work Order: <span className="font-mono text-white" data-testid="wo-cancel-docno">{cancelPrompt?.po_no || "—"}</span>
            </div>
            <div>
              <label className="block text-xs font-mono uppercase tracking-widest text-zinc-400 mb-1">
                Cancellation Reason <span className="text-red-400">*</span>
              </label>
              <textarea
                data-testid="wo-cancel-reason"
                value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value)}
                rows={3}
                placeholder="Enter the reason for cancelling this Work Order (min 4 characters)"
                className="w-full px-3 py-2 bg-[var(--bg-0)] border border-[var(--border)] rounded-sm text-white text-sm focus:outline-none focus:border-red-500/60"
              />
            </div>
            <div className="text-xs text-amber-300/90 bg-amber-500/10 border border-amber-500/30 rounded-sm px-3 py-2">
              Warning: Cancelling this Work Order will stop it from being used for new production activity according to existing Work Order cancellation rules.
            </div>
          </div>
          <DialogFooter className="gap-2">
            <button
              data-testid="wo-cancel-back"
              onClick={() => { setCancelPrompt(null); setCancelReason(""); }}
              className="px-4 py-2 rounded-sm border border-[var(--border)] text-sm text-zinc-400"
            >Back</button>
            <button
              data-testid="wo-cancel-confirm"
              onClick={confirmCancel}
              disabled={cancelBusy || (cancelReason || "").trim().length < 4}
              className="px-4 py-2 rounded-sm bg-red-600 hover:bg-red-700 disabled:bg-zinc-700 disabled:cursor-not-allowed text-sm font-semibold text-white flex items-center gap-2"
            >
              <ProhibitIcon size={14} weight="bold" /> Cancel Work Order
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>


      {/* Feb 2026 · Dynamic Order Modification popover for WOs */}
      {editId && (
        <ModifyDocumentPopover
          docType="WO"
          docId={editId}
          docNo={form.po_no}
          open={modifyOpen}
          onClose={() => setModifyOpen(false)}
          onDone={async () => { setOpen(false); await load(); }}
          onLineCancel={async () => { await load(); }}
          testidPrefix="wo-modify"
          allow={{ unapprove: hasPerm(null, "production_orders:unapprove"), close: getUser()?.role === "Administrator" }}
        />
      )}
      <WoRejectDialog wo={rejectWo} onClose={() => setRejectWo(null)} onDone={load} />

      {/* Feb 2026 · Phase 2 — Explode BOM + Child Work Orders modal */}
      <Dialog open={!!explWo} onOpenChange={(o) => !o && closeExplode()}>
        <DialogContent className="bg-[var(--bg-1)] border-[var(--border)] text-white max-w-[95vw] max-h-[92vh] overflow-y-auto" data-testid="wo-explode-dialog" description="Work Order action — targets, machines, material issue and print.">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-3">
              <TreeStructureIcon size={20} weight="bold" className="text-sky-400" />
              Explode BOM · {explWo?.po_no}
              {/* Customer hidden on production floor · Mar 2026 · Tiered Visibility */}
              {explData?.production_qty !== undefined && (
                <span className="ml-auto text-xs font-mono text-emerald-300">Prod Qty · {explData.production_qty}</span>
              )}
            </DialogTitle>
          </DialogHeader>

          {explLoading && (
            <div className="py-16 text-center text-zinc-500 text-sm">Exploding BOMs…</div>
          )}

          {!explLoading && explData && (
            <div className="space-y-5">
              {/* Feb 2026 · Per-WO Routing Overrides — Warping / Weaving
                  can be pinned to a specific floor on this WO without
                  touching global defaults. Saved to wo.godown_overrides
                  and re-stamped onto every Open MRQ under this WO. */}
              <div className="rounded-sm border border-[var(--border)] bg-[var(--bg-2)]/40 p-3" data-testid="wo-routing-overrides">
                <div className="flex items-center justify-between mb-2">
                  <div className="overline text-[10px]">Routing Overrides</div>
                  <button
                    data-testid="wo-routing-save"
                    onClick={saveOverrides}
                    className="text-[10px] px-2 py-1 rounded-sm border border-[var(--accent)]/50 bg-[var(--accent)]/10 hover:bg-[var(--accent)]/20 text-[var(--accent)] font-mono uppercase tracking-widest"
                  >
                    Save & Re-route MRQs
                  </button>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {OVERRIDE_STAGES.map(s => (
                    <div key={s.code}>
                      <label className="block text-[10px] text-zinc-500 mb-1">{s.label}</label>
                      <select
                        data-testid={`wo-routing-${s.code.toLowerCase()}`}
                        value={overrides[s.code] || ""}
                        onChange={e => setOverrides(o => ({ ...o, [s.code]: e.target.value }))}
                        className="w-full h-8 px-2 bg-[var(--bg-0)] border border-[var(--border)] rounded-sm text-xs focus:outline-none focus:border-[var(--accent)]"
                      >
                        {s.opts.map(([v, l]) => <option key={v} value={v}>{l}{v ? ` · ${v}` : ""}</option>)}
                      </select>
                    </div>
                  ))}
                </div>
                <div className="mt-2 text-[10px] text-zinc-500">Blank = global default. Overrides re-stamp destination on every Open MRQ under this WO the moment you save.</div>
              </div>
              {/* Missing BOMs warning */}
              {explData.missing_boms?.length > 0 && (
                <div className="rounded-sm border border-amber-500/40 bg-amber-500/5 px-3 py-2 text-[11px] text-amber-200" data-testid="wo-explode-missing">
                  <div className="font-mono uppercase tracking-widest text-amber-300 mb-1">
                    {explData.missing_boms.length} item(s) without an active BOM — Child WOs won't roll up their materials
                  </div>
                  <ul className="list-disc list-inside space-y-0.5">
                    {explData.missing_boms.map((m, i) => (
                      <li key={i}><span className="font-mono">{m.product_code}</span> · {m.item_description}</li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Feb 2026 · Per-Product Breakdown — Product Code /
                  Order Qty (from SO) / Production Qty (planned with
                  rejection + extra buffer) / BOM No. of Ends / Covering
                  type. Each product expands into its BOM lines showing
                  master No. of Ends and the auto-doubled Yarn Ends when
                  the product is flagged as Double Covering. */}
              <div>
                <div className="text-[11px] font-mono uppercase tracking-widest text-zinc-400 mb-2 flex items-center gap-2">
                  <TreeStructureIcon size={12} weight="bold" />Per-Product Breakdown · {(explData.by_item || []).length} item(s)
                </div>
                <div className="space-y-3" data-testid="wo-product-breakdown">
                  {(explData.by_item || []).map((it, i) => (
                    <div key={i} className="border border-[var(--border)] rounded-sm overflow-hidden" data-testid={`wo-product-card-${i}`}>
                      <div className="bg-[var(--bg-2)]/40 px-3 py-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs">
                        <div className="font-mono text-[var(--accent)]" data-testid={`wo-product-code-${i}`}>{it.product_code || "—"}</div>
                        {it.item_description && <div className="text-zinc-400 truncate max-w-xs" title={it.item_description}>{it.item_description}</div>}
                        <div className="ml-auto flex items-center gap-4 text-[11px] font-mono">
                          <div><span className="text-zinc-500 uppercase tracking-widest text-[9px] mr-1">Order</span><span className="text-white tabular-nums" data-testid={`wo-product-order-qty-${i}`}>{Number(it.order_qty || 0).toLocaleString("en-IN", { maximumFractionDigits: 3 })}</span></div>
                          <div><span className="text-zinc-500 uppercase tracking-widest text-[9px] mr-1">Prod</span><span className="text-emerald-300 tabular-nums" data-testid={`wo-product-prod-qty-${i}`}>{Number(it.production_qty || 0).toLocaleString("en-IN", { maximumFractionDigits: 3 })}</span></div>
                          <div><span className="text-zinc-500 uppercase tracking-widest text-[9px] mr-1">BOM Ends</span><span className="text-white tabular-nums">{Number(it.bom_no_of_ends || 0)}</span></div>
                        </div>
                      </div>
                      {it.status === "NO ACTIVE BOM" ? (
                        <div className="px-3 py-3 text-[11px] text-amber-300 bg-amber-500/5">No active BOM found — cannot resolve Yarn Ends for this product.</div>
                      ) : (it.bom_lines_detail || []).length === 0 ? (
                        <div className="px-3 py-3 text-[11px] text-zinc-500">BOM has no lines.</div>
                      ) : (
                        <div className="overflow-x-auto">
                          <table className="w-full text-xs" data-testid={`wo-product-lines-${i}`}>
                            <thead className="bg-[var(--bg-2)]/20 text-[10px] uppercase tracking-widest text-zinc-500">
                              <tr>
                                <th className="px-3 py-2 text-left w-10">#</th>
                                <th className="px-3 py-2 text-left w-16">Type</th>
                                <th className="px-3 py-2 text-left">RM / SF Code</th>
                                <th className="px-3 py-2 text-left">Description</th>
                                <th className="px-3 py-2 text-right w-24">No. of Ends</th>
                                <th className="px-3 py-2 text-right w-28">Yarn Ends</th>
                                <th className="px-3 py-2 text-right w-24">Wt / Metre (g)</th>
                                <th className="px-3 py-2 text-right w-28">Total Wt (kg)</th>
                              </tr>
                            </thead>
                            <tbody>
                              {(it.bom_lines_detail || []).map((bl, j) => (
                                <tr key={j} className="border-t border-[var(--border)]" data-testid={`wo-product-line-${i}-${j}`}>
                                  <td className="px-3 py-2 font-mono text-zinc-500">{bl.line_no}</td>
                                  <td className="px-3 py-2">
                                    <span className={`text-[10px] font-mono uppercase tracking-widest px-1.5 py-0.5 rounded-sm border ${bl.is_spandex ? "border-purple-500/30 bg-purple-500/10 text-purple-300" : bl.item_type === "SF" ? "border-sky-500/30 bg-sky-500/10 text-sky-300" : "border-zinc-500/30 text-zinc-300"}`}>
                                      {bl.is_spandex ? "Spandex" : bl.item_type}
                                    </span>
                                  </td>
                                  <td className="px-3 py-2 font-mono text-white truncate max-w-[16rem]" title={bl.rm_code}>
                                    <div className="flex items-center gap-2">
                                      <span className="truncate">{bl.rm_code || "—"}</span>
                                      {bl.covering_type === "DOUBLE" && (
                                        <span
                                          className="px-1.5 py-0.5 rounded-sm border border-orange-500/40 bg-orange-500/10 text-orange-300 text-[9px] font-mono uppercase tracking-widest"
                                          title="Covering set to DOUBLE on this SF/BOM — Yarn Ends auto-doubled"
                                        >
                                          DOUBLE
                                        </span>
                                      )}
                                    </div>
                                  </td>
                                  <td className="px-3 py-2 text-zinc-300 truncate max-w-[20rem]" title={bl.rm_name}>{bl.rm_name || "—"}</td>
                                  <td className="px-3 py-2 text-right font-mono tabular-nums text-zinc-400">
                                    {bl.no_of_ends ? Number(bl.no_of_ends).toLocaleString("en-IN") : "—"}
                                  </td>
                                  <td
                                    className={`px-3 py-2 text-right font-mono tabular-nums font-semibold ${bl.is_doubled ? "bg-amber-500/15 text-amber-200" : "text-white"}`}
                                    title={bl.is_doubled ? "×2 multiplier applied for Double Covering" : ""}
                                    data-testid={`wo-yarn-ends-${i}-${j}`}
                                  >
                                    {bl.is_doubled
                                      ? <span>{Number(bl.no_of_ends).toLocaleString("en-IN")}<span className="text-amber-400"> × 2</span></span>
                                      : (bl.calculated_ends ? Number(bl.calculated_ends).toLocaleString("en-IN") : "—")}
                                  </td>
                                  <td className="px-3 py-2 text-right font-mono tabular-nums text-zinc-400" data-testid={`wo-wpm-${i}-${j}`}>
                                    {bl.gms_per_metre ? fmtG(bl.gms_per_metre) : "—"}
                                  </td>
                                  <td className="px-3 py-2 text-right font-mono tabular-nums text-emerald-300" data-testid={`wo-total-wt-${i}-${j}`}>
                                    {bl.total_weight_kg ? fmtKg(bl.total_weight_kg) : "—"}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                            <tfoot className="border-t-2 border-[var(--border)] bg-[var(--bg-2)]/40">
                              <tr data-testid={`wo-product-totals-${i}`}>
                                <td colSpan={6} className="px-3 py-2 text-right text-[10px] font-mono uppercase tracking-widest text-zinc-500">Totals</td>
                                <td className="px-3 py-2 text-right font-mono tabular-nums text-zinc-200 font-semibold" data-testid={`wo-total-wpm-${i}`}>
                                  {fmtG((it.bom_lines_detail || []).reduce((s, x) => s + (Number(x.gms_per_metre) || 0), 0))}
                                </td>
                                <td className="px-3 py-2 text-right font-mono tabular-nums text-emerald-300 font-semibold" data-testid={`wo-total-weight-${i}`}>
                                  {fmtKg((it.bom_lines_detail || []).reduce((s, x) => s + (Number(x.total_weight_kg) || 0), 0))}
                                </td>
                              </tr>
                            </tfoot>
                          </table>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {/* Phase 3J.9A · Warping Conversion Preview — one row per Warping RM
                  BOM line showing INPUT RM → PROCESS → OUTPUT SFW.
                  This appears BEFORE the Stage Roll-up so the planner
                  can verify the RM → SFW mapping before Generate Child
                  Work Orders. Level-1 remains the RM source (spec §3). */}
              {(explData.warping_preview || []).length > 0 && (
                <div data-testid="wo-warping-preview">
                  <div className="text-[11px] font-mono uppercase tracking-widest text-zinc-400 mb-2 flex items-center gap-2">
                    <TreeStructureIcon size={12} weight="bold" />
                    Warping Conversion Preview · {explData.warping_preview.length} line(s)
                    <span className="ml-2 text-[10px] text-zinc-500">INPUT RM → WARPING → OUTPUT SFW</span>
                  </div>
                  <div className="border border-sky-500/30 rounded-sm overflow-x-auto bg-sky-500/5">
                    <table className="w-full text-xs" data-testid="wo-warping-preview-table">
                      <thead className="bg-[var(--bg-2)]/40 text-[10px] uppercase tracking-widest text-zinc-500">
                        <tr>
                          <th className="px-2 py-2 text-left">Input RM</th>
                          <th className="px-2 py-2 text-left">Description</th>
                          <th className="px-2 py-2 text-right w-24">Planned Qty</th>
                          <th className="px-2 py-2 text-left w-14">UoM</th>
                          <th className="px-2 py-2 text-right w-16">Ends</th>
                          <th className="px-2 py-2 text-center w-20">Process</th>
                          <th className="px-2 py-2 text-left">Output SFW</th>
                          <th className="px-2 py-2 text-left w-24">Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {explData.warping_preview.map((wp, i) => (
                          <tr key={i} className="border-t border-[var(--border)]" data-testid={`wo-warping-preview-row-${i}`}>
                            <td className="px-2 py-2 font-mono text-[var(--accent)] truncate max-w-[18rem]" title={wp.input_rm_code} data-testid={`wo-wp-input-rm-${i}`}>
                              {wp.input_rm_code || <span className="text-rose-400">— MISSING —</span>}
                            </td>
                            <td className="px-2 py-2 text-zinc-300 truncate max-w-[18rem]" title={wp.input_rm_description}>
                              {wp.input_rm_description || "—"}
                            </td>
                            <td className="px-2 py-2 text-right font-mono tabular-nums text-emerald-300" data-testid={`wo-wp-qty-${i}`}>
                              {Number(wp.planned_rm_qty || 0).toLocaleString("en-IN", { maximumFractionDigits: 3 })}
                            </td>
                            <td className="px-2 py-2 text-zinc-400">{wp.uom || "kg"}</td>
                            <td className="px-2 py-2 text-right font-mono tabular-nums text-white" data-testid={`wo-wp-ends-${i}`}>
                              {wp.no_of_ends > 0
                                ? Number(wp.no_of_ends).toLocaleString("en-IN")
                                : <span className="text-rose-400">—</span>}
                            </td>
                            <td className="px-2 py-2 text-center">
                              <span className="text-[10px] font-mono uppercase tracking-widest px-1.5 py-0.5 rounded-sm border border-sky-500/40 bg-sky-500/10 text-sky-300">
                                {wp.process || "Warping"}
                              </span>
                            </td>
                            <td className="px-2 py-2 font-mono text-cyan-300 truncate max-w-[22rem]" title={wp.output_sfw_code} data-testid={`wo-wp-output-sfw-${i}`}>
                              {wp.output_sfw_code || <span className="text-rose-400">— UNRESOLVED —</span>}
                              {wp.output_sfw_exists && (
                                <span className="ml-2 text-[9px] font-mono uppercase tracking-widest px-1 py-0.5 rounded-sm border border-emerald-500/40 bg-emerald-500/10 text-emerald-300" title="Reuses existing sf_master row">
                                  EXISTS
                                </span>
                              )}
                            </td>
                            <td className="px-2 py-2" data-testid={`wo-wp-status-${i}`}>
                              {wp.readiness === "READY" ? (
                                <span className="text-[10px] font-mono uppercase tracking-widest px-1.5 py-0.5 rounded-sm border border-emerald-500/40 bg-emerald-500/10 text-emerald-300">READY</span>
                              ) : (
                                <span className="text-[10px] font-mono uppercase tracking-widest px-1.5 py-0.5 rounded-sm border border-rose-500/40 bg-rose-500/10 text-rose-300" title={wp.readiness_reason}>
                                  NOT READY · {wp.readiness_reason || wp.readiness}
                                </span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <div className="mt-1 text-[10px] text-zinc-500">
                    Planned Qty · wastage-adjusted RM requirement from the Main BOM Level-1 line. Level-1 continues to feed MRP + procurement.
                  </div>
                </div>
              )}

              {/* Phase 3J.9B · Spandex Warping Conversion Preview — one row
                  per Spandex Warping Level-1 SF BOM line showing INPUT SFC
                  → PROCESS → OUTPUT SFSW. Rendered directly below the
                  Warping preview so the planner reviews both preparatory
                  conversion contracts in the same section (spec §11 · §29). */}
              {(explData.spandex_warping_preview || []).length > 0 && (
                <div data-testid="wo-spandex-warping-preview">
                  <div className="text-[11px] font-mono uppercase tracking-widest text-zinc-400 mb-2 flex items-center gap-2">
                    <TreeStructureIcon size={12} weight="bold" />
                    Spandex Warping Conversion Preview · {explData.spandex_warping_preview.length} line(s)
                    <span className="ml-2 text-[10px] text-zinc-500">INPUT SFC → SPANDEX WARPING → OUTPUT SFSW</span>
                  </div>
                  <div className="border border-purple-500/30 rounded-sm overflow-x-auto bg-purple-500/5">
                    <table className="w-full text-xs" data-testid="wo-spandex-warping-preview-table">
                      <thead className="bg-[var(--bg-2)]/40 text-[10px] uppercase tracking-widest text-zinc-500">
                        <tr>
                          <th className="px-2 py-2 text-left">Input SFC</th>
                          <th className="px-2 py-2 text-left w-20">Source</th>
                          <th className="px-2 py-2 text-right w-24">Planned Qty</th>
                          <th className="px-2 py-2 text-left w-14">UoM</th>
                          <th className="px-2 py-2 text-right w-16">Ends</th>
                          <th className="px-2 py-2 text-center w-28">Process</th>
                          <th className="px-2 py-2 text-left">Output SFSW</th>
                          <th className="px-2 py-2 text-left w-24">Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {explData.spandex_warping_preview.map((sw, i) => (
                          <tr key={i} className="border-t border-[var(--border)]" data-testid={`wo-sw-preview-row-${i}`}>
                            <td className="px-2 py-2 font-mono text-purple-300 truncate max-w-[18rem]" title={sw.input_sfc_code} data-testid={`wo-sw-input-sfc-${i}`}>
                              {sw.input_sfc_code || <span className="text-rose-400">— MISSING —</span>}
                            </td>
                            <td className="px-2 py-2">
                              <span className="text-[10px] font-mono uppercase tracking-widest px-1.5 py-0.5 rounded-sm border border-fuchsia-500/40 bg-fuchsia-500/10 text-fuchsia-300" title={sw.secondary_bom_no ? `${sw.secondary_bom_no} v${sw.secondary_bom_version}` : ""}>
                                {sw.source_level || "Level-2"}
                              </span>
                            </td>
                            <td className="px-2 py-2 text-right font-mono tabular-nums text-emerald-300" data-testid={`wo-sw-qty-${i}`}>
                              {Number(sw.planned_input_qty || 0).toLocaleString("en-IN", { maximumFractionDigits: 3 })}
                            </td>
                            <td className="px-2 py-2 text-zinc-400">{sw.uom || "kg"}</td>
                            <td className="px-2 py-2 text-right font-mono tabular-nums text-white" data-testid={`wo-sw-ends-${i}`}>
                              {sw.no_of_ends > 0
                                ? Number(sw.no_of_ends).toLocaleString("en-IN")
                                : <span className="text-rose-400">—</span>}
                            </td>
                            <td className="px-2 py-2 text-center">
                              <span className="text-[10px] font-mono uppercase tracking-widest px-1.5 py-0.5 rounded-sm border border-purple-500/40 bg-purple-500/10 text-purple-300">
                                {sw.process || "Spandex Warping"}
                              </span>
                            </td>
                            <td className="px-2 py-2 font-mono text-cyan-300 truncate max-w-[22rem]" title={sw.output_sfsw_code} data-testid={`wo-sw-output-sfsw-${i}`}>
                              {sw.output_sfsw_code || <span className="text-rose-400">— UNRESOLVED —</span>}
                              {sw.output_sfsw_exists && (
                                <span className="ml-2 text-[9px] font-mono uppercase tracking-widest px-1 py-0.5 rounded-sm border border-emerald-500/40 bg-emerald-500/10 text-emerald-300" title="Reuses existing sf_master row">
                                  EXISTS
                                </span>
                              )}
                            </td>
                            <td className="px-2 py-2" data-testid={`wo-sw-status-${i}`}>
                              {sw.readiness === "READY" ? (
                                <span className="text-[10px] font-mono uppercase tracking-widest px-1.5 py-0.5 rounded-sm border border-emerald-500/40 bg-emerald-500/10 text-emerald-300">READY</span>
                              ) : (
                                <span className="text-[10px] font-mono uppercase tracking-widest px-1.5 py-0.5 rounded-sm border border-rose-500/40 bg-rose-500/10 text-rose-300" title={sw.readiness_reason}>
                                  NOT READY · {sw.readiness_reason || sw.readiness}
                                </span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <div className="mt-1 text-[10px] text-zinc-500">
                    Input SFC · from Level-2 (Secondary) Covering BOM output. SFSW code · {'{'}source_sfc_code{'}'}-{'{'}ends{'}'}ends (auto-locked). Never uses raw Spandex or Covering Yarn (spec §19).
                  </div>
                </div>
              )}

              {/* Stage Roll-up */}
              <div>
                <div className="text-[11px] font-mono uppercase tracking-widest text-zinc-400 mb-2 flex items-center gap-2">
                  <StackIcon size={12} weight="bold" />Routing Stage Roll-up · aggregated across all items in this WO
                </div>
                <div className="border border-[var(--border)] rounded-sm overflow-x-auto">
                  <table className="w-full text-xs" data-testid="wo-stage-summary">
                    <thead className="bg-[var(--bg-2)]/40 text-[10px] uppercase tracking-widest text-zinc-500">
                      <tr>
                        <th className="px-2 py-2 text-left w-32">Stage</th>
                        <th className="px-2 py-2 text-left w-24">Kind</th>
                        <th className="px-2 py-2 text-left w-24">Parallel Group</th>
                        <th className="px-2 py-2 text-left">Depends On</th>
                        <th className="px-2 py-2 text-right w-28">Total Qty</th>
                        <th className="px-2 py-2 text-left w-16">UoM</th>
                        <th className="px-2 py-2 text-left">RMs Consumed</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(explData.stage_totals || []).map(s => (
                        <tr key={s.stage_code} className="border-t border-[var(--border)]">
                          <td className="px-2 py-2 font-mono text-white">{s.stage_code}</td>
                          <td className="px-2 py-2 text-zinc-300">{s.kind || "—"}</td>
                          <td className="px-2 py-2">
                            {s.parallel_group ? (
                              <span className="text-[10px] font-mono uppercase tracking-widest px-1.5 py-0.5 rounded-sm border border-sky-500/30 bg-sky-500/10 text-sky-300">{s.parallel_group}</span>
                            ) : <span className="text-zinc-600">—</span>}
                          </td>
                          <td className="px-2 py-2 font-mono text-xs text-zinc-400">{s.depends_on || <span className="text-zinc-600">—</span>}</td>
                          <td className="px-2 py-2 text-right font-mono tabular-nums text-emerald-300">{(s.uom || "").toLowerCase() === "kg" ? fmtKg(s.gross_qty) : fmtG(s.gross_qty)}</td>
                          <td className="px-2 py-2 text-zinc-400">{s.uom}</td>
                          <td className="px-2 py-2 text-[11px] font-mono text-zinc-500 truncate max-w-md" title={s.rm_codes?.join(", ")}>{s.rm_codes?.slice(0, 3).join(", ")}{s.rm_codes?.length > 3 ? ` +${s.rm_codes.length - 3}` : ""}</td>
                        </tr>
                      ))}
                      {(!explData.stage_totals || explData.stage_totals.length === 0) && (
                        <tr><td colSpan={7} className="text-center py-8 text-zinc-500">No routing stages — assign a Routing on each BOM line first</td></tr>
                      )}
                    </tbody>
                    {(explData.stage_totals || []).length > 0 && (
                      <tfoot className="border-t-2 border-[var(--border)] bg-[var(--bg-2)]/40">
                        <tr data-testid="wo-stage-total">
                          <td colSpan={4} className="px-2 py-2 text-right text-[10px] font-mono uppercase tracking-widest text-zinc-500">Total</td>
                          <td className="px-2 py-2 text-right font-mono tabular-nums text-emerald-300 font-semibold">
                            {fmtKg((explData.stage_totals || []).reduce((s, x) => s + (Number(x.gross_qty) || 0), 0))}
                          </td>
                          <td className="px-2 py-2 text-zinc-400 text-[10px]">kg</td>
                          <td></td>
                        </tr>
                      </tfoot>
                    )}
                  </table>
                </div>
              </div>

              {/* RM Roll-up */}
              <div>
                <div className="text-[11px] font-mono uppercase tracking-widest text-zinc-400 mb-2">Raw Material Roll-up · {explData.rm_totals?.length || 0} distinct RM(s)</div>
                <div className="border border-[var(--border)] rounded-sm overflow-x-auto max-h-72 overflow-y-auto">
                  <table className="w-full text-xs" data-testid="wo-rm-summary">
                    <thead className="bg-[var(--bg-2)]/40 text-[10px] uppercase tracking-widest text-zinc-500 sticky top-0">
                      <tr>
                        <th className="px-2 py-2 text-left">RM Code</th>
                        <th className="px-2 py-2 text-left">RM Name</th>
                        <th className="px-2 py-2 text-right w-28">Total Qty</th>
                        <th className="px-2 py-2 text-left w-16">UoM</th>
                        <th className="px-2 py-2 text-left w-40">Stages</th>
                        <th className="px-2 py-2 text-left">Used by</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(explData.rm_totals || []).map(r => (
                        <tr key={r.rm_id || r.rm_code} className="border-t border-[var(--border)]">
                          <td className="px-2 py-2 font-mono text-[var(--accent)]">{r.rm_code}</td>
                          <td className="px-2 py-2 text-zinc-300">{r.rm_name}</td>
                          <td className="px-2 py-2 text-right font-mono tabular-nums text-white">{(r.uom || "").toLowerCase() === "kg" ? fmtKg(r.gross_qty) : fmtG(r.gross_qty)}</td>
                          <td className="px-2 py-2 text-zinc-400">{r.uom}</td>
                          <td className="px-2 py-2 font-mono text-[10px] text-zinc-400">{(r.stages || []).join(" · ")}</td>
                          <td className="px-2 py-2 text-[11px] text-zinc-500 truncate max-w-md" title={r.used_by_items?.join(", ")}>{r.used_by_items?.slice(0, 2).join(", ")}{r.used_by_items?.length > 2 ? ` +${r.used_by_items.length - 2}` : ""}</td>
                        </tr>
                      ))}
                      {(!explData.rm_totals || explData.rm_totals.length === 0) && (
                        <tr><td colSpan={6} className="text-center py-8 text-zinc-500">No RM requirements resolved</td></tr>
                      )}
                    </tbody>
                    {(explData.rm_totals || []).length > 0 && (
                      <tfoot className="border-t-2 border-[var(--border)] bg-[var(--bg-2)]/40">
                        <tr data-testid="wo-rm-total">
                          <td colSpan={2} className="px-2 py-2 text-right text-[10px] font-mono uppercase tracking-widest text-zinc-500">Total</td>
                          <td className="px-2 py-2 text-right font-mono tabular-nums text-white font-semibold">
                            {fmtKg((explData.rm_totals || []).reduce((s, x) => s + (Number(x.gross_qty) || 0), 0))}
                          </td>
                          <td className="px-2 py-2 text-zinc-400 text-[10px]">kg</td>
                          <td colSpan={2}></td>
                        </tr>
                      </tfoot>
                    )}
                  </table>
                </div>
              </div>

              {/* Child Work Orders — with Generate button */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <div className="text-[11px] font-mono uppercase tracking-widest text-zinc-400">
                    Child Work Orders · {childWos.length} linked
                  </div>
                  <button
                    data-testid="wo-generate-cwos"
                    onClick={generateChildWos}
                    disabled={genLoading || !(explData.stage_totals || []).filter(s => s.stage_code !== "UNASSIGNED").length}
                    title={
                      !(explData.stage_totals || []).filter(s => s.stage_code !== "UNASSIGNED").length
                        ? "Assign a Routing on each BOM line first"
                        : ""
                    }
                    className="h-8 px-3 bg-[var(--accent)] hover:bg-blue-600 disabled:bg-zinc-700 disabled:cursor-not-allowed text-white text-xs font-semibold rounded-sm flex items-center gap-2"
                  >
                    <PlusIcon size={12} weight="bold" />
                    {childWos.length > 0 ? "Refresh from BOM" : "Generate Child WOs"}
                  </button>
                </div>
                {/* Contextual hint when disabled — spells out exactly why. */}
                {!(explData.stage_totals || []).filter(s => s.stage_code !== "UNASSIGNED").length && (
                  <div className="mb-2 rounded-sm border border-amber-500/40 bg-amber-500/5 px-3 py-2 text-[11px] text-amber-200" data-testid="cwo-generate-hint">
                    <span className="font-mono uppercase tracking-widest text-amber-300">Can't generate yet · </span>
                    {explData.missing_boms?.length > 0
                      ? "One or more items in this WO have no active BOM — author a BOM for them in BOM Master first."
                      : "Every BOM line's Routing is blank or Unassigned — open BOM Master and pick a Routing Stage (Warping / Covering / Weaving / …) on each line."}
                  </div>
                )}
                <div className="border border-[var(--border)] rounded-sm overflow-x-auto">
                  <table className="w-full text-xs" data-testid="wo-child-wos">
                    <thead className="bg-[var(--bg-2)]/40 text-[10px] uppercase tracking-widest text-zinc-500">
                      <tr>
                        <th className="px-2 py-2 text-left w-64">Child WO No</th>
                        <th className="px-2 py-2 text-left w-28">Date</th>
                        <th className="px-2 py-2 text-left">Stage</th>
                        <th className="px-2 py-2 text-left w-24">Parallel</th>
                        <th className="px-2 py-2 text-left">Depends On</th>
                        <th className="px-2 py-2 text-right w-28">Qty Planned</th>
                        <th className="px-2 py-2 text-left w-16">UoM</th>
                        <th className="px-2 py-2 text-left w-32">Status</th>
                        <th className="px-2 py-2 text-right w-40">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {childWos.map(c => {
                        const tone = ({
                          Ready: "border-sky-500/30 bg-sky-500/10 text-sky-300",
                          Pending: "border-zinc-500/30 bg-zinc-500/10 text-zinc-300",
                          "In Progress": "border-amber-500/30 bg-amber-500/10 text-amber-300",
                          Completed: "border-emerald-500/30 bg-emerald-500/10 text-emerald-300",
                          Cancelled: "border-red-500/30 bg-red-500/10 text-red-300",
                        })[c.status] || "border-zinc-500/30 text-zinc-400";
                        return (
                          <tr key={c.id} className="border-t border-[var(--border)]" data-testid={`cwo-row-${c.stage_code}`}>
                            <td className="px-2 py-2 text-right"><TwoLineDocNo value={c.cwo_no} className="text-white" /></td>
                            <td className="px-2 py-2 font-mono text-zinc-300 whitespace-nowrap" data-testid={`cwo-date-${c.stage_code}`}>{fmtDateIST(c.created_at)}</td>
                            <td className="px-2 py-2 text-zinc-300">{c.stage_name} <span className="text-zinc-600">· {c.stage_code}</span></td>
                            <td className="px-2 py-2">
                              {c.parallel_group ? (
                                <span className="text-[10px] font-mono uppercase tracking-widest px-1.5 py-0.5 rounded-sm border border-sky-500/30 bg-sky-500/10 text-sky-300">{c.parallel_group}</span>
                              ) : <span className="text-zinc-600">—</span>}
                            </td>
                            <td className="px-2 py-2 font-mono text-[10px] text-zinc-400">{c.depends_on || <span className="text-zinc-600">—</span>}</td>
                            <td className="px-2 py-2 text-right font-mono tabular-nums text-white">{Number(c.qty_planned).toLocaleString("en-IN", { maximumFractionDigits: 3 })}</td>
                            <td className="px-2 py-2 text-zinc-400">{c.uom}</td>
                            <td className="px-2 py-2">
                              <span className={`text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 rounded-sm border ${tone}`}>{c.status}</span>
                            </td>
                            <td className="px-2 py-2 text-right space-x-1">
                              {c.status === "Ready" && (
                                <button title="Start" data-testid={`cwo-start-${c.stage_code}`} onClick={() => patchChildWo(c, "In Progress")}
                                  className="px-2 py-1 rounded-sm border border-amber-500/40 hover:bg-amber-500/10 text-amber-300 text-[10px] font-mono uppercase tracking-widest">Start</button>
                              )}
                              {c.status === "In Progress" && (
                                <button title="Complete" data-testid={`cwo-complete-${c.stage_code}`} onClick={() => patchChildWo(c, "Completed")}
                                  className="px-2 py-1 rounded-sm border border-emerald-500/40 hover:bg-emerald-500/10 text-emerald-300 text-[10px] font-mono uppercase tracking-widest">Complete</button>
                              )}
                              {c.status === "Pending" && (
                                <span className="text-[10px] text-zinc-500 font-mono">Awaiting deps</span>
                              )}
                              {c.status === "Completed" && (
                                <span className="text-[10px] text-emerald-400 font-mono">✓ Done</span>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                      {childWos.length === 0 && (
                        <tr><td colSpan={9} className="text-center py-8 text-zinc-500">No Child Work Orders yet — click <span className="text-white font-mono">Generate Child WOs</span> to emit one per stage</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
                <div className="text-[10px] text-zinc-600 mt-2">
                  Preparatory stages (Warping / Covering / Spandex Warping) start as <span className="text-sky-300">Ready</span> and run in parallel. Downstream stages (Weaving, Dyeing, Finishing, <span className="text-fuchsia-300">Printing</span> — Path D only, Packing) auto-flip to <span className="text-sky-300">Ready</span> when every dependency reaches <span className="text-emerald-300">Completed</span>. Printing carries a 2-day lead-time buffer for batch runs.
                </div>
              </div>
            </div>
          )}

          <DialogFooter>
            <button onClick={closeExplode} className="px-4 py-2 rounded-sm border border-[var(--border)] text-sm text-zinc-400">Close</button>
            {explData && (
              <button
                data-testid="wo-explode-pdf"
                onClick={printExplodePdf}
                title="Open a printable PDF of this Explode BOM view"
                className="px-4 py-2 rounded-sm border border-[var(--border)] hover:bg-[var(--bg-2)] text-sm text-zinc-200 flex items-center gap-2"
              >
                <PrinterIcon size={14} weight="bold" />Print PDF
              </button>
            )}
            {explData && (
              <button
                data-testid="wo-generate-cwos-footer"
                onClick={generateChildWos}
                disabled={genLoading || !(explData.stage_totals || []).filter(s => s.stage_code !== "UNASSIGNED").length}
                title={
                  !(explData.stage_totals || []).filter(s => s.stage_code !== "UNASSIGNED").length
                    ? "Assign a Routing on each BOM line first — Child WOs are generated per stage"
                    : (childWos.length ? "Refresh child WO quantities from the latest BOM" : "Emit one Child WO per Routing Stage")
                }
                className="px-4 py-2 rounded-sm bg-[var(--accent)] hover:bg-blue-600 disabled:bg-zinc-700 disabled:cursor-not-allowed text-sm font-semibold text-white flex items-center gap-2"
              >
                <TreeStructureIcon size={14} weight="bold" />
                {genLoading ? "Working…" : (childWos.length ? `Refresh ${childWos.length} Child WOs` : "Generate Child Work Orders")}
              </button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* WO-AMEND-HISTORY-UI-H01 · Feb 2026 · Amendment History Dialog
          Read-only renderer of history data already present on the WO
          document. NO mutations. NO Edit/Delete/Restore/Reverse/Resubmit/
          Approve/Reject/Cancel/Amend buttons inside. */}
      <Dialog open={!!historyWo} onOpenChange={(o) => !o && setHistoryWo(null)}>
        <DialogContent className="bg-[var(--bg-1)] border-[var(--border)] max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="wo-history-dialog" description="Read-only chronological history of Management Rejections, Corrections, Reconciliations and Amendments recorded on this Work Order.">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2">
              <ClockCounterClockwiseIcon size={18} weight="bold" />
              Work Order Amendment History — {historyWo?.wo_no || historyWo?.po_no}
            </DialogTitle>
          </DialogHeader>
          {(() => {
            const wo = historyWo || {};
            const amendments = Array.isArray(wo.amendments) ? wo.amendments : [];
            const hasMgmtRejectAmendment = amendments.some(a => a?.action === "mgmt_reject");
            // Owner-mandated event classification (Decision §3).
            const classify = (a) => {
              switch (a?.action) {
                case "mgmt_reject":                return { label: "Management Rejection",             tone: "bg-red-500/10 border-red-500/30 text-red-300",     Icon: ProhibitIcon,      technical: false };
                case "mgmt_reject_resubmit":       return { label: "Correction & Resubmission",        tone: "bg-amber-500/10 border-amber-500/30 text-amber-300", Icon: PencilSimpleIcon, technical: false };
                case "reconcile_reject_lifecycle": return { label: "Defect Reconciliation (Technical)", tone: "bg-zinc-500/10 border-zinc-500/30 text-zinc-300",   Icon: WrenchIcon,        technical: true  };
                default:                           return { label: "Business Amendment",                tone: "bg-sky-500/10 border-sky-500/30 text-sky-300",       Icon: NotePencilIcon,    technical: false };
              }
            };
            // Chronological, newest first.
            const ordered = amendments.slice().reverse();
            return (
              <div className="space-y-3">
                {/* Currently Authorised banner (parity with SO history). */}
                {wo.approved_by && (
                  <div className="p-3 rounded-sm bg-emerald-500/5 border border-emerald-500/20" data-testid="wo-history-authorised-banner">
                    <div className="flex items-center gap-2">
                      <CheckCircleIcon size={14} weight="bold" className="text-emerald-400" />
                      <span className="text-sm font-semibold text-emerald-400">Currently Authorised</span>
                      <span className="ml-auto text-[10px] font-mono px-1.5 py-0.5 rounded-sm bg-emerald-500/10 text-emerald-400">{wo.status || "Approved"}</span>
                    </div>
                    <div className="text-xs text-zinc-400 mt-1">
                      by <span className="font-mono">{wo.approved_by}</span>{wo.approved_at ? <> on <span className="font-mono">{wo.approved_at}</span></> : null}
                    </div>
                  </div>
                )}

                {/* Original Management Rejection top-level scalars — rendered
                    ONLY when the same event is NOT already represented in
                    amendments[] to avoid duplicate chronological entries. */}
                {wo.rejected_by && !hasMgmtRejectAmendment && (
                  <div className="p-3 rounded-sm bg-red-500/5 border border-red-500/20" data-testid="wo-history-original-rejection">
                    <div className="flex items-center gap-2">
                      <ProhibitIcon size={14} weight="bold" className="text-red-400" />
                      <span className="text-sm font-semibold text-red-300">Original Management Rejection</span>
                    </div>
                    <div className="text-xs text-zinc-400 mt-1">
                      by <span className="font-mono">{wo.rejected_by}</span>{wo.rejected_at ? <> on <span className="font-mono">{wo.rejected_at}</span></> : null}
                    </div>
                    {wo.rejection_comments && (
                      <div className="text-sm text-zinc-300 mt-2"><span className="text-zinc-500 text-xs">Reason:</span> {wo.rejection_comments}</div>
                    )}
                  </div>
                )}

                {/* No history at all */}
                {ordered.length === 0 && !wo.approved_by && !wo.rejected_by && (
                  <div className="text-sm text-zinc-500 text-center py-8" data-testid="wo-history-empty">
                    No amendment history recorded for this Work Order.
                  </div>
                )}

                {/* Amendments (newest first) */}
                {ordered.map((a, idx) => {
                  const cls = classify(a);
                  const Icon = cls.Icon;
                  return (
                    <div
                      key={a.amendment_no || `${a.date}-${idx}`}
                      className={`p-3 rounded-sm border ${cls.tone}`}
                      data-testid={`wo-amendment-${a.amendment_no || idx}`}
                    >
                      <div className="flex items-center justify-between mb-2 gap-2">
                        <span className="font-mono font-semibold text-white/90">{a.amendment_no || `#${amendments.length - idx}`}</span>
                        <span className="ml-auto inline-flex items-center gap-1 px-1.5 py-0.5 rounded-sm text-[10px] font-mono uppercase tracking-widest border" data-testid={`wo-amendment-category-${a.amendment_no || idx}`}>
                          <Icon size={11} weight="bold" />
                          {cls.label}
                          {cls.technical && (
                            <span className="ml-1 px-1 rounded-sm bg-zinc-500/20 text-zinc-300 text-[9px]">TECHNICAL</span>
                          )}
                        </span>
                        {a.date && <span className="text-[10px] font-mono text-zinc-500">{a.date}</span>}
                      </div>
                      {a.by && (
                        <div className="text-xs text-zinc-400 mb-1"><span className="text-zinc-500">By:</span> <span className="font-mono">{a.by}</span></div>
                      )}
                      {a.reason && (
                        <div className="text-sm text-zinc-300 mb-2 break-words"><span className="text-zinc-500 text-xs">Reason:</span> {a.reason}</div>
                      )}
                      {/* Owner Decision §3 — Previous / New values ONLY when
                          stored. Never invent, never reconstruct. */}
                      {(a.previous_value !== undefined || a.new_value !== undefined) && (
                        <div className="mt-2 grid grid-cols-2 gap-2 text-xs">
                          {a.previous_value !== undefined && (
                            <div className="p-2 rounded-sm bg-[var(--bg-0)] border border-[var(--border)]">
                              <div className="overline text-[9px] text-zinc-500 mb-1">Previous</div>
                              <div className="font-mono text-zinc-300 break-words">{typeof a.previous_value === "object" ? JSON.stringify(a.previous_value) : String(a.previous_value)}</div>
                            </div>
                          )}
                          {a.new_value !== undefined && (
                            <div className="p-2 rounded-sm bg-[var(--bg-0)] border border-[var(--border)]">
                              <div className="overline text-[9px] text-zinc-500 mb-1">New</div>
                              <div className="font-mono text-zinc-300 break-words">{typeof a.new_value === "object" ? JSON.stringify(a.new_value) : String(a.new_value)}</div>
                            </div>
                          )}
                        </div>
                      )}
                      {Array.isArray(a.changed_fields) && a.changed_fields.length > 0 && (
                        <div className="mt-2 text-xs">
                          <div className="text-zinc-500 mb-1">Changed fields:</div>
                          <div className="flex flex-wrap gap-1">
                            {a.changed_fields.map(f => (
                              <span key={f} className="px-1.5 py-0.5 rounded-sm bg-[var(--bg-0)] font-mono text-[10px] text-zinc-300">{f}</span>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            );
          })()}
          <DialogFooter>
            <button onClick={() => setHistoryWo(null)} data-testid="wo-history-close" className="px-4 py-2 rounded-sm bg-[var(--accent)] text-sm font-semibold text-white">Close</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Kpi({ label, value, suffix, tone, testid }) {
  const toneMap = {
    zinc: "border-zinc-500/30 bg-zinc-500/5",
    emerald: "border-emerald-500/30 bg-emerald-500/5",
    red: "border-red-500/30 bg-red-500/5",
    blue: "border-blue-500/30 bg-blue-500/5",
  };
  return (
    <div data-testid={testid} className={`tactical-card px-4 py-3 border ${toneMap[tone] || ""}`}>
      <div className="text-[10px] uppercase tracking-widest text-zinc-500">{label}</div>
      <div className="mt-1 flex items-baseline gap-1">
        <div className="font-display text-2xl font-black text-white">{value}</div>
        {suffix && <div className="text-[10px] uppercase tracking-widest text-zinc-500">{suffix}</div>}
      </div>
    </div>
  );
}
